import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ethers } from 'ethers';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAccount, useSignMessage, useWriteContract } from 'wagmi';
import ConnectWalletButton from '../../components/connectWallet/ConnectWalletButton';
import SwitchNetworkButton from '../../components/connectWallet/SwitchNetworkButton';
import { JOBS_PORTAL_PATH } from '../../config/paths';
import { useJobsPortalRole } from '../../context/JobsPortalRoleContext';
import { ERC20ABI, TaskEscrowABI } from '../../ABI/ABI';
import {
    TASK_ESCROW_ADDRESS,
    targetChainId,
    WalletAddressContext,
} from '../../constant/constant';
import Loader from '../../components/loader/Loader';
import { publicClient } from '../../utils/wagmiConfig';
import { waitForSuccessfulTransactionReceipt } from '../../utils/transactionReceipt';
import { ensureExactTokenAllowance } from '../../utils/erc20Approval';
import { readContractData } from '../../utils/contractInstance';
import {
    backendApi,
    connectWalletSession,
    getStoredBackendToken,
    storeBackendToken,
} from '../../api/backendClient';
import {
    copyToClipboard,
    fireErrorToast,
    fireInfoToast,
    fireSuccessToast,
    formatNumber,
    shortenWalletAddress,
} from '../../utils/commonFunction';
import {
    canCompanyReclaimUnacceptedEscrow,
    canEmployeeSubmitWork,
    canEmployeeClaimJobPayment,
    canRaiseJobDispute,
    DEFAULT_ESCROW_RECLAIM_DELAY_SECONDS,
    formatReclaimCountdown,
    formatReclaimDelayLabel,
    formatTaskStatus,
    isEscrowAcceptedOnChain,
    isTaskInHistory,
    normalizeJobEscrowState,
    normalizeJobWorkState,
    resolveTaskStatus,
} from './helpers';
import CompanyProfileFormFields from '../../components/profile/CompanyProfileFormFields';
import { resolveAttachmentUrl } from '../../utils/attachmentUrl';
import {
    getNotificationLink,
    getNotificationIcon,
} from '../../utils/notificationMeta';
import {
    buildCompanyPayload,
    buildEmployeePayload,
    buildEmployeeSelfPayload,
    companyToForm,
    employeeToEditForm,
    employeeToSelfForm,
    validateSalariedMonthlySalary,
} from '../../utils/profileFormMaps';
import {
    emptyCompanyForm,
    emptyEditEmployeeForm,
    emptyEmployeeForm,
    emptyEmployeeSelfForm,
    emptyTaskForm,
    formFieldClass,
} from './constants';
import EmployeeSelfProfileModal from './components/EmployeeSelfProfileModal';
import { normalizeWallet, formatDateTime } from './formatting';
import { buildTaskDescription, parseTaskDescription } from './taskDescription';
import { extractEscrowIdFromReceipt } from './escrowUtils';
import {
    JOB_ESCROW_FN,
    registerCompanyOnChain,
    registerEmployeeOnChain,
    readCompanyRegistryId,
    readEmployeeRegistryId,
    readCanAssignPayrollTask,
    readEmployeeEmploymentClass,
    readPayrollEnrollmentId,
    diagnoseSalariedAssignmentBlockers,
    updateEmployeeEmploymentClassOnChain,
    resolveJobsPaymentToken,
    enrollAndFundPayrollOnChain,
    depositPayrollOnChain,
    createJobAssignmentOnChain,
    explainJobsContractError,
    EMPLOYMENT_CLASS_ON_CHAIN,
    loadEmployeePayrollStatus,
    claimPayrollOnChain,
} from '../../utils/jobsDiamond';
import TaskCardCompact from './components/TaskCardCompact';
import CreateTaskPage from './components/CreateTaskPage';
import {
    validateCreateTaskForm,
    hasCreateTaskFormErrors,
    isSalariedEmployee,
} from './utils/validateCreateTaskForm';
import EmployeePayrollCard from './components/EmployeePayrollCard';
import TaskReviewPage from './components/TaskReviewPage';
import PaginatedTaskSection from './components/PaginatedTaskSection';
import JobsWorkspaceDashboard from './components/JobsWorkspaceDashboard';
import JobsStatsPage from './components/JobsStatsPage';
import JobsEarningsPage from './components/JobsEarningsPage';
import JobsProfilePage from './components/JobsProfilePage';
import TaskHistoryPage from './components/TaskHistoryPage';
import EmployeesDirectory from './components/EmployeesDirectory';
import AddEmployeePage from './components/AddEmployeePage';
import EditEmployeePage from './components/EditEmployeePage';
import EmployeeDetailPage from './components/EmployeeDetailPage';
import RaiseDisputeModal from './components/RaiseDisputeModal';
import RequestReworkModal from './components/RequestReworkModal';
import { getTasksForEmployee } from './utils/employeeTaskMetrics';
import {
    clearEncryptionKeyPairCache,
    decryptTasksInList,
    decryptTaskPayload,
    deriveEncryptionKeyPair,
    prepareEncryptedTaskCreatePayload,
    taskNeedsClientDecrypt,
} from '../../utils/walletTaskEncryption';

/** Icon + color for workspace activity timeline nodes */
function workspaceActivityIconMeta(notification) {
    const blob = `${notification.type || ''} ${notification.title || ''} ${notification.message || ''} ${notification.body || ''} ${notification.description || ''}`.toLowerCase();
    if (blob.includes('rework') || blob.includes('resubmit')) {
        return { icon: 'fa-solid fa-rotate', ring: 'ring-amber-500/25 dark:ring-amber-400/20' };
    }
    if (blob.includes('task')) {
        return { icon: 'fa-regular fa-clipboard', ring: 'ring-teal-500/25 dark:ring-teal-400/20' };
    }
    if (blob.includes('pay') || blob.includes('fund') || blob.includes('escrow') || blob.includes('token')) {
        return { icon: 'fa-regular fa-coins', ring: 'ring-zinc-400/30 dark:ring-zinc-500/25' };
    }
    if (blob.includes('dispute')) {
        return { icon: 'fa-regular fa-scale-balanced', ring: 'ring-amber-500/20 dark:ring-amber-400/15' };
    }
    if (blob.includes('employ')) {
        return { icon: 'fa-regular fa-user', ring: 'ring-zinc-400/25 dark:ring-zinc-500/20' };
    }
    return { icon: 'fa-regular fa-bell', ring: 'ring-zinc-300/40 dark:ring-zinc-600/30' };
}

const CompanyJob = () => {
    const { isAdmin: isCpgAdmin } = useContext(WalletAddressContext);
    const { isConnected, address: walletAddress, chainId } = useAccount();
    const { signMessageAsync } = useSignMessage();
    const { writeContractAsync } = useWriteContract();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    const [authLoading, setAuthLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [employeeStatusUpdatingId, setEmployeeStatusUpdatingId] = useState(null);
    const [activeAction, setActiveAction] = useState('');
    const [backendToken, setBackendToken] = useState('');
    const [company, setCompany] = useState(null);
    const [employeeCompany, setEmployeeCompany] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [companyTasks, setCompanyTasks] = useState([]);
    const [employeeTasks, setEmployeeTasks] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [taskListRefreshKey, setTaskListRefreshKey] = useState(0);
    const [tokenInfo, setTokenInfo] = useState({
        address: '',
        name: 'Payment Token',
        symbol: 'TOKEN',
        decimals: 6,
        allowance: 0n,
        balance: 0n,
    });
    const [adminAddress, setAdminAddress] = useState('');
    const [acceptReclaimDelaySeconds, setAcceptReclaimDelaySeconds] = useState(
        DEFAULT_ESCROW_RECLAIM_DELAY_SECONDS,
    );
    const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
    const [employeeForm, setEmployeeForm] = useState(emptyEmployeeForm);
    const [taskForm, setTaskForm] = useState(emptyTaskForm);
    const [activeWorkspaceTab, setActiveWorkspaceTab] = useState('setup');
    const [activeTaskView, setActiveTaskView] = useState('my');

    // UI-only join selection for first time UX.
    // Actual "can create company" rule is enforced by backend.
    const [joinChoice, setJoinChoice] = useState(null);

    // Popup modals (company creation/join actions).
    const [companyModalOpen, setCompanyModalOpen] = useState(false);
    const [editEmployeeForm, setEditEmployeeForm] = useState(emptyEditEmployeeForm);
    const [myEmployeeRecord, setMyEmployeeRecord] = useState(null);
    const [employeeSelfForm, setEmployeeSelfForm] = useState(emptyEmployeeSelfForm);
    const [employeeSelfModalOpen, setEmployeeSelfModalOpen] = useState(false);
    /** Prevents the complete-profile prompt from reopening after save or dismiss. */
    const employeeSelfProfilePromptRef = useRef({
        suppressed: false,
        opened: false,
    });
    const createTaskInFlightRef = useRef(false);
    const acceptTaskInFlightRef = useRef(false);
    const [payrollAssignable, setPayrollAssignable] = useState(true);
    const [payrollFundingId, setPayrollFundingId] = useState(null);
    const [employeePayrollStatus, setEmployeePayrollStatus] = useState(null);
    const [employeePayrollLoading, setEmployeePayrollLoading] = useState(false);
    const [employeePayrollClaiming, setEmployeePayrollClaiming] = useState(false);
    const [taskFormErrors, setTaskFormErrors] = useState({});
    const [taskFormSubmitError, setTaskFormSubmitError] = useState('');
    const [raiseDisputeModal, setRaiseDisputeModal] = useState({
        open: false,
        task: null,
    });
    const [requestReworkModal, setRequestReworkModal] = useState({
        open: false,
        task: null,
    });
    const [detailTaskLoading, setDetailTaskLoading] = useState(false);
    /** Full task detail + actions (same pattern as incoming payment request modal). */
    const openTaskDetail = useCallback(
        (task) => {
            if (!task?.id) return;
            setSearchParams(
                (prev) => {
                    const n = new URLSearchParams(prev);
                    n.set('taskId', String(task.id));
                    n.delete('panel');
                    return n;
                },
                { replace: false },
            );
        },
        [setSearchParams],
    );

    const closeTaskDetail = useCallback(() => {
        setSearchParams(
            (prev) => {
                const n = new URLSearchParams(prev);
                n.delete('taskId');
                n.delete('panel');
                return n;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    const openSubmitTaskForm = useCallback(
        (task) => {
            if (!task?.id) return;
            setSearchParams(
                (prev) => {
                    const n = new URLSearchParams(prev);
                    n.set('taskId', String(task.id));
                    n.set('panel', 'submit');
                    n.delete('section');
                    return n;
                },
                { replace: false },
            );
        },
        [setSearchParams],
    );

    const closeSubmitTaskForm = useCallback(() => {
        setSearchParams(
            (prev) => {
                const n = new URLSearchParams(prev);
                n.delete('panel');
                return n;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    const openEmployeeDetail = useCallback(
        (employee) => {
            if (!employee?.id) return;
            setSearchParams(
                (prev) => {
                    const n = new URLSearchParams(prev);
                    n.set('section', 'setup');
                    n.set('panel', 'employee');
                    n.delete('taskId');
                    n.set('employeeId', String(employee.id));
                    return n;
                },
                { replace: false },
            );
        },
        [setSearchParams],
    );

    const closeEmployeeDetail = useCallback(() => {
        setSearchParams(
            (prev) => {
                const n = new URLSearchParams(prev);
                n.delete('employeeId');
                return n;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    const contractAddress = TASK_ESCROW_ADDRESS?.trim();
    const contractConfigured = Boolean(
        contractAddress && ethers.isAddress(contractAddress),
    );
    const normalizedWallet = normalizeWallet(walletAddress);
    const normalizedAdmin = normalizeWallet(adminAddress);
    const isAdmin = Boolean(
        normalizedWallet &&
        normalizedAdmin &&
        normalizedWallet === normalizedAdmin,
    );

    const effectiveJoinMode = company
        ? 'company'
        : employeeCompany
            ? 'employee'
            : joinChoice;
    const isCompanyOwner = Boolean(
        company &&
        normalizeWallet(company.ownerWallet) === normalizedWallet,
    );
    const openCompanyJobRoute = useCallback(
        (updates) => {
            setSearchParams(
                (prev) => {
                    const n = new URLSearchParams(prev);
                    Object.entries(updates).forEach(([key, value]) => {
                        if (value == null || value === '') {
                            n.delete(key);
                        } else {
                            n.set(key, String(value));
                        }
                    });
                    if (!Object.prototype.hasOwnProperty.call(updates, 'taskId')) {
                        n.delete('taskId');
                    }
                    if (!Object.prototype.hasOwnProperty.call(updates, 'employeeId')) {
                        n.delete('employeeId');
                    }
                    const sec = n.get('section');
                    if (sec === 'create') {
                        n.delete('panel');
                        n.delete('view');
                    } else if (sec === 'setup') {
                        n.delete('view');
                    } else if (
                        sec === 'workspace' &&
                        n.get('panel') === 'activity'
                    ) {
                        n.delete('view');
                    } else if (
                        sec === 'stats' ||
                        sec === 'history' ||
                        sec === 'earnings' ||
                        sec === 'profile'
                    ) {
                        n.delete('panel');
                        n.delete('view');
                    }
                    return n;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );
    const openAddEmployee = useCallback(() => {
        openCompanyJobRoute({ section: 'setup', panel: 'add-employee' });
    }, [openCompanyJobRoute]);
    const closeAddEmployee = useCallback(() => {
        openCompanyJobRoute({ section: 'setup', panel: 'employee' });
    }, [openCompanyJobRoute]);

    const closeEditEmployee = useCallback(({ forceList = false } = {}) => {
        const returnTo = forceList
            ? 'list'
            : searchParams.get('editReturn') || 'list';
        setSearchParams(
            (prev) => {
                const n = new URLSearchParams(prev);
                n.delete('editReturn');
                n.set('section', 'setup');
                n.set('panel', 'employee');
                if (returnTo !== 'detail') {
                    n.delete('employeeId');
                }
                return n;
            },
            { replace: true },
        );
    }, [searchParams, setSearchParams]);

    const openEditEmployee = useCallback(
        (employee, { returnTo = 'list' } = {}) => {
            if (!employee?.id) return;
            setEditEmployeeForm(employeeToEditForm(employee));
            setSearchParams(
                (prev) => {
                    const n = new URLSearchParams(prev);
                    n.set('section', 'setup');
                    n.set('panel', 'edit-employee');
                    n.set('employeeId', String(employee.id));
                    n.set('editReturn', returnTo);
                    n.delete('taskId');
                    return n;
                },
                { replace: false },
            );
        },
        [setSearchParams],
    );

    const navigateToEmployeeDetailAfterEdit = useCallback(
        (employeeId) => {
            setSearchParams(
                (prev) => {
                    const n = new URLSearchParams(prev);
                    n.delete('editReturn');
                    n.set('section', 'setup');
                    n.set('panel', 'employee');
                    n.set('employeeId', String(employeeId));
                    return n;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );
    const isEmployeeActive = (employee) =>
        String(employee?.status || 'active').toLowerCase() === 'active';
    const hasJoinSelection = Boolean(effectiveJoinMode);
    const jobsPortalRole = useMemo(() => {
        if (!hasJoinSelection) return 'guest';
        if (effectiveJoinMode === 'company' || effectiveJoinMode === 'employee') {
            return effectiveJoinMode;
        }
        return 'guest';
    }, [hasJoinSelection, effectiveJoinMode]);

    const { setJobsPortalRole } = useJobsPortalRole();
    useEffect(() => {
        setJobsPortalRole(jobsPortalRole);
        return () => setJobsPortalRole('guest');
    }, [jobsPortalRole, setJobsPortalRole]);

    const navigate = useNavigate();

    useEffect(() => {
        if (location.pathname !== JOBS_PORTAL_PATH) return;

        const sec = searchParams.get('section') || 'setup';
        const panel = searchParams.get('panel') || '';
        const view = searchParams.get('view') || '';

        const resetToJoin = () => {
            navigate(`${JOBS_PORTAL_PATH}?section=setup`, { replace: true });
        };

        if (jobsPortalRole === 'guest') {
            if (
                sec === 'create' ||
                sec === 'workspace' ||
                sec === 'stats' ||
                sec === 'history' ||
                sec === 'earnings' ||
                sec === 'profile'
            ) {
                resetToJoin();
                return;
            }
            if (
                sec === 'setup' &&
                (panel === 'company' ||
                    panel === 'employee' ||
                    panel === 'add-employee' ||
                    panel === 'edit-employee')
            ) {
                resetToJoin();
            }
            return;
        }

        if (jobsPortalRole === 'employee') {
            if (
                panel === 'company' ||
                panel === 'employee' ||
                panel === 'add-employee' ||
                panel === 'edit-employee'
            ) {
                resetToJoin();
                return;
            }
            if (sec === 'create') {
                navigate(
                    `${JOBS_PORTAL_PATH}?section=workspace&panel=tasks&view=assigned`,
                    { replace: true },
                );
                return;
            }
            if (sec === 'workspace' && panel === 'tasks' && view === 'company') {
                navigate(
                    `${JOBS_PORTAL_PATH}?section=workspace&panel=tasks&view=assigned`,
                    { replace: true },
                );
                return;
            }
            if (sec === 'workspace' && panel === 'tasks' && view === 'my') {
                navigate(
                    `${JOBS_PORTAL_PATH}?section=workspace&panel=tasks&view=assigned`,
                    { replace: true },
                );
            }
        }

        if (jobsPortalRole === 'company') {
            if (sec === 'stats') {
                navigate(`${JOBS_PORTAL_PATH}?section=setup`, { replace: true });
                return;
            }
            if (sec === 'setup' && panel === 'company') {
                navigate(`${JOBS_PORTAL_PATH}?section=profile`, { replace: true });
                return;
            }
            if (
                sec === 'workspace' &&
                panel === 'tasks' &&
                (view === 'assigned' || view === 'my' || !view)
            ) {
                navigate(
                    `${JOBS_PORTAL_PATH}?section=workspace&panel=tasks&view=company`,
                    { replace: true },
                );
            }
        }
    }, [location.pathname, jobsPortalRole, navigate, searchParams]);

    const activeSetupPanel = searchParams.get('panel') || 'overview';

    useEffect(() => {
        if (activeSetupPanel !== 'add-employee') return;
        if (!isCompanyOwner || effectiveJoinMode !== 'company') {
            closeAddEmployee();
        }
    }, [
        activeSetupPanel,
        isCompanyOwner,
        effectiveJoinMode,
        closeAddEmployee,
    ]);

    useEffect(() => {
        if (activeSetupPanel !== 'edit-employee') return;
        if (!isCompanyOwner || effectiveJoinMode !== 'company') {
            closeEditEmployee();
        }
    }, [
        activeSetupPanel,
        isCompanyOwner,
        effectiveJoinMode,
        closeEditEmployee,
    ]);

    const showJoinedDashboard =
        hasJoinSelection &&
        activeWorkspaceTab === 'setup' &&
        (!activeSetupPanel || activeSetupPanel === 'overview');

    const requestedAmount = useMemo(() => {
        try {
            if (!taskForm.amount) return 0n;
            return ethers.parseUnits(
                taskForm.amount || '0',
                tokenInfo.decimals,
            );
        } catch (error) {
            return 0n;
        }
    }, [taskForm.amount, tokenInfo.decimals]);

    const hasEnoughAllowance =
        requestedAmount > 0n && tokenInfo.allowance >= requestedAmount;
    const hasEnoughBalance =
        requestedAmount > 0n && tokenInfo.balance >= requestedAmount;

    useEffect(() => {
        let cancelled = false;

        const checkPayrollAssignable = async () => {
            const selected = employees.find(
                (employee) =>
                    String(employee.id) === String(taskForm.employeeId),
            );
            if (
                !selected ||
                !isSalariedEmployee(selected) ||
                !contractConfigured ||
                !walletAddress
            ) {
                if (!cancelled) setPayrollAssignable(true);
                return;
            }

            try {
                const ok = await readCanAssignPayrollTask(
                    contractAddress,
                    walletAddress,
                    selected.wallet,
                );
                if (!cancelled) setPayrollAssignable(Boolean(ok));
            } catch (_error) {
                if (!cancelled) setPayrollAssignable(false);
            }
        };

        checkPayrollAssignable();
        return () => {
            cancelled = true;
        };
    }, [
        taskForm.employeeId,
        employees,
        contractConfigured,
        walletAddress,
        contractAddress,
    ]);

    const allTasks = useMemo(() => {
        const uniqueMap = new Map();
        [...companyTasks, ...employeeTasks].forEach((task) =>
            uniqueMap.set(task.id, task),
        );
        return Array.from(uniqueMap.values()).sort(
            (first, second) =>
                new Date(second.createdAt || 0).getTime() -
                new Date(first.createdAt || 0).getTime(),
        );
    }, [companyTasks, employeeTasks]);

    const selectedTaskId = searchParams.get('taskId');
    const selectedEmployeeId = searchParams.get('employeeId');
    const selectedTask = useMemo(() => {
        if (!selectedTaskId) return null;
        return allTasks.find((t) => String(t.id) === String(selectedTaskId)) ?? null;
    }, [allTasks, selectedTaskId]);

    const canSubmitSelectedTask = useMemo(
        () => canEmployeeSubmitWork(selectedTask, normalizedWallet),
        [selectedTask, normalizedWallet],
    );

    const selectedEmployee = useMemo(() => {
        if (!selectedEmployeeId) return null;
        return (
            employees.find((e) => String(e.id) === String(selectedEmployeeId)) ?? null
        );
    }, [employees, selectedEmployeeId]);

    const selectedEmployeeWithTasks = useMemo(() => {
        if (!selectedEmployee) return null;
        return {
            ...selectedEmployee,
            tasks: getTasksForEmployee(companyTasks, selectedEmployee),
        };
    }, [selectedEmployee, companyTasks]);

    const isEditingEmployee =
        activeSetupPanel === 'edit-employee' && Boolean(selectedEmployeeId);

    const taskReviewBackLabel = useMemo(() => {
        if (selectedEmployeeId) return 'Back to employee';
        if (resolveTaskStatus(selectedTask) === 'submitted') {
            return 'Back';
        }
        return 'Back to tasks';
    }, [selectedTask, selectedEmployeeId]);

    const myTasks = useMemo(
        () =>
            allTasks.filter(
                (task) =>
                    normalizeWallet(task.company?.ownerWallet) ===
                    normalizedWallet ||
                    normalizeWallet(task.employee?.wallet) === normalizedWallet,
            ),
        [allTasks, normalizedWallet],
    );

    const disputedTasks = useMemo(
        () => allTasks.filter((task) => resolveTaskStatus(task) === 'disputed'),
        [allTasks],
    );

    const roleDisputedTasks = useMemo(() => {
        const source =
            effectiveJoinMode === 'company' ? companyTasks : employeeTasks;
        return source.filter((task) => resolveTaskStatus(task) === 'disputed');
    }, [effectiveJoinMode, companyTasks, employeeTasks]);

    const stats = useMemo(
        () => ({
            totalLocked: companyTasks
                .filter((task) => resolveTaskStatus(task) !== 'released')
                .reduce((sum, task) => sum + Number(task.amount || 0), 0),
            totalEarned: employeeTasks
                .filter((task) => resolveTaskStatus(task) === 'released')
                .reduce((sum, task) => sum + Number(task.amount || 0), 0),
            awaitingRelease: employeeTasks.filter(
                (task) => resolveTaskStatus(task) === 'submitted',
            ).length,
            openDisputes: myTasks.filter(
                (task) => resolveTaskStatus(task) === 'disputed',
            ).length,
        }),
        [companyTasks, employeeTasks, myTasks],
    );

    const taskDraftPreview = useMemo(
        () => parseTaskDescription(buildTaskDescription(taskForm)),
        [taskForm],
    );

    const activeTasksOnly = useCallback(
        (tasks) => tasks.filter((task) => !isTaskInHistory(task)),
        [],
    );

    const historyTasks = useMemo(() => {
        const source =
            effectiveJoinMode === 'company' ? companyTasks : employeeTasks;
        return source
            .filter(isTaskInHistory)
            .sort((a, b) => {
                const ta = new Date(b.updatedAt || b.createdAt || 0).getTime();
                const tb = new Date(a.updatedAt || a.createdAt || 0).getTime();
                return ta - tb;
            });
    }, [effectiveJoinMode, companyTasks, employeeTasks]);

    const portalEarningsTasks = useMemo(
        () => (effectiveJoinMode === 'company' ? companyTasks : employeeTasks),
        [effectiveJoinMode, companyTasks, employeeTasks],
    );

    const employeeProfile = useMemo(() => {
        if (effectiveJoinMode !== 'employee') return null;
        if (myEmployeeRecord) return myEmployeeRecord;
        const match = employeeTasks.find(
            (task) =>
                normalizeWallet(task.employee?.wallet) === normalizedWallet,
        );
        return match?.employee || null;
    }, [effectiveJoinMode, employeeTasks, normalizedWallet, myEmployeeRecord]);

    const taskViews = useMemo(() => {
        const views = [
            {
                key: 'my',
                title: 'My Tasks',
                description:
                    'Tasks linked to your current account.',
                items: activeTasksOnly(myTasks),
                emptyMessage:
                    'No task records found for this wallet yet.',
            },
            {
                key: 'company',
                title: 'Created tasks',
                description:
                    'Tasks created by your company and assigned to employees.',
                items: activeTasksOnly(companyTasks),
                emptyMessage:
                    'No company tasks yet. Create a task to assign work to your team.',
            },
            {
                key: 'assigned',
                title: 'Assigned To You',
                description:
                    'Tasks currently assigned to you.',
                items: activeTasksOnly(employeeTasks),
                emptyMessage:
                    'No tasks are assigned to this wallet yet.',
            },
            {
                key: 'disputed',
                title: 'Disputed',
                description:
                    effectiveJoinMode === 'company'
                        ? 'Company tasks in dispute awaiting resolution.'
                        : 'Your assigned tasks currently in dispute.',
                items: roleDisputedTasks,
                emptyMessage: 'No disputed tasks right now.',
            },
            isAdmin
                ? {
                    key: 'admin-disputes',
                    title: 'Admin Dispute Queue',
                    description:
                        'All disputed tasks across the workspace.',
                    items: disputedTasks,
                    emptyMessage:
                        'There are no disputed tasks right now.',
                }
                : null,
        ].filter(Boolean);

        if (effectiveJoinMode === 'employee') {
            return views.filter((v) =>
                ['assigned', 'disputed', 'admin-disputes'].includes(v.key),
            );
        }
        if (effectiveJoinMode === 'company') {
            return views.filter((v) =>
                ['company', 'disputed', 'admin-disputes'].includes(v.key),
            );
        }
        return views;
    }, [
        myTasks,
        companyTasks,
        employeeTasks,
        isAdmin,
        disputedTasks,
        roleDisputedTasks,
        effectiveJoinMode,
        activeTasksOnly,
    ]);

    const selectedTaskView =
        taskViews.find((view) => view.key === activeTaskView) || taskViews[0];

    const resolveTaskListFetcher = useCallback(
        (viewKey) => {
            if (viewKey === 'assigned') {
                return backendApi.getEmployeeTasks;
            }
            if (effectiveJoinMode === 'employee') {
                return backendApi.getEmployeeTasks;
            }
            return backendApi.getCompanyTasks;
        },
        [effectiveJoinMode],
    );

    const resolveTaskListScope = (viewKey) => {
        if (viewKey === 'disputed' || viewKey === 'admin-disputes') {
            return 'disputed';
        }
        return 'active';
    };

    const tasksAssignedToMe = useMemo(
        () =>
            allTasks.filter(
                (task) =>
                    normalizeWallet(task.employee?.wallet) === normalizedWallet,
            ),
        [allTasks, normalizedWallet],
    );

    const tasksCreatedByMe = useMemo(
        () =>
            allTasks.filter(
                (task) =>
                    normalizeWallet(
                        task.createdByWallet || task.company?.ownerWallet,
                    ) === normalizedWallet,
            ),
        [allTasks, normalizedWallet],
    );

    const getReadableError = (error) => {
        const explained = explainJobsContractError(error);
        const lower = String(explained || '').toLowerCase();
        if (lower.includes('rate limit')) {
            return 'RPC provider is rate limited. Wait a minute and try again, or switch your wallet/network RPC to the fallback endpoint.';
        }
        return explained;
    };

    const isJwtExpiredError = (error) => {
        const statusCode = Number(error?.response?.status || 0);
        const message = String(
            error?.response?.data?.message || error?.message || '',
        ).toLowerCase();

        return (
            statusCode === 401 &&
            (message.includes('jwt expired') ||
                message.includes('token expired') ||
                message.includes('invalid token') ||
                message.includes('user not found') ||
                message.includes('wallet signature'))
        );
    };

    const createFreshSessionToken = async () => {
        const session = await connectWalletSession({
            walletAddress,
            signMessageAsync,
            // Profile preference only — server never grants admin from this field.
            rolePreference: 'employee',
        });
        storeBackendToken(walletAddress, session.token);
        setBackendToken(session.token);
        return session.token;
    };

    const updateCompanyForm = (event) => {
        const { name, value } = event.target;
        setCompanyForm((current) => ({ ...current, [name]: value }));
    };

    const openCompanyModal = useCallback(() => {
        setCompanyForm(company ? companyToForm(company) : emptyCompanyForm);
        setCompanyModalOpen(true);
    }, [company]);

    const openEmployeeSelfModal = useCallback(() => {
        setEmployeeSelfForm(
            myEmployeeRecord
                ? employeeToSelfForm(myEmployeeRecord)
                : emptyEmployeeSelfForm,
        );
        setEmployeeSelfModalOpen(true);
    }, [myEmployeeRecord]);

    const updateEmployeeSelfForm = (event) => {
        const { name, value } = event.target;
        setEmployeeSelfForm((current) => ({ ...current, [name]: value }));
    };
    const updateEmployeeForm = (event) => {
        const { name, value } = event.target;
        setEmployeeForm((current) => ({ ...current, [name]: value }));
    };
    const updateEditEmployeeForm = (event) => {
        const { name, value } = event.target;
        setEditEmployeeForm((current) => ({ ...current, [name]: value }));
    };

    const updateTaskForm = (event) => {
        const { name, value } = event.target;
        setTaskForm((current) => ({ ...current, [name]: value }));
    };

    const handleTaskFormChange = (event) => {
        updateTaskForm(event);
        const field = event.target?.name;
        if (field && taskFormErrors[field]) {
            setTaskFormErrors((current) => {
                const next = { ...current };
                delete next[field];
                return next;
            });
        }
        if (taskFormSubmitError) {
            setTaskFormSubmitError('');
        }
    };

    const activeEmployeeCount = useMemo(
        () => employees.filter((employee) => isEmployeeActive(employee)).length,
        [employees, isEmployeeActive],
    );

    const safeReadContractData = async (
        address,
        abi,
        functionName,
        args = [],
    ) => {
        try {
            return await readContractData(address, abi, functionName, args);
        } catch (error) {
            return null;
        }
    };
    const hydrateTaskWithChainData = useCallback(
        async (task) => {
            if (!contractConfigured || !task.escrowId) {
                return {
                    ...task,
                    onChain: null,
                    amountFormatted: Number(task.amount || 0),
                    amountLabel: formatNumber(Number(task.amount || 0)),
                    statusLabel:
                        task.escrowId && resolveTaskStatus(task) === 'rejected'
                            ? 'Rework Requested'
                            : formatTaskStatus(resolveTaskStatus(task)),
                };
            }

            const [onChainEscrowRaw, onChainWorkRaw] = await Promise.all([
                safeReadContractData(
                    contractAddress,
                    TaskEscrowABI,
                    'escrows',
                    [Number(task.escrowId)],
                ),
                safeReadContractData(
                    contractAddress,
                    TaskEscrowABI,
                    'getJobWorkState',
                    [Number(task.escrowId)],
                ),
            ]);
            const onChainEscrow = normalizeJobEscrowState(onChainEscrowRaw);
            const onChainWork = normalizeJobWorkState(onChainWorkRaw);
            const normalizedTask = {
                ...task,
                onChain: onChainEscrow,
                onChainWork,
                amountFormatted: Number(task.amount || 0),
                amountLabel: formatNumber(Number(task.amount || 0)),
            };

            return {
                ...normalizedTask,
                statusLabel:
                    normalizedTask.escrowId &&
                    resolveTaskStatus(normalizedTask) === 'rejected'
                        ? 'Rework Requested'
                        : formatTaskStatus(resolveTaskStatus(normalizedTask)),
            };
        },
        [contractAddress, contractConfigured],
    );

    const refreshTokenState = async () => {
        if (
            !contractConfigured ||
            !walletAddress ||
            chainId !== targetChainId
        ) {
            return tokenInfo;
        }

        const [tokenAddress, disputeAdmin, contractOwner, reclaimDelayRaw] =
            await Promise.all([
            resolveJobsPaymentToken(contractAddress),
            safeReadContractData(
                contractAddress,
                TaskEscrowABI,
                'disputeAdmin',
                [],
            ),
            safeReadContractData(contractAddress, TaskEscrowABI, 'owner', []),
            safeReadContractData(
                contractAddress,
                TaskEscrowABI,
                'acceptReclaimDelay',
                [],
            ),
        ]);

        const normalizedTokenAddress =
            typeof tokenAddress === 'string' ? tokenAddress.trim() : '';
        const hasValidTokenAddress = ethers.isAddress(normalizedTokenAddress);

        const [name, symbol, decimalsValue, allowance, balance] =
            hasValidTokenAddress
                ? await Promise.all([
                    safeReadContractData(
                        normalizedTokenAddress,
                        ERC20ABI,
                        'name',
                        [],
                    ),
                    safeReadContractData(
                        normalizedTokenAddress,
                        ERC20ABI,
                        'symbol',
                        [],
                    ),
                    safeReadContractData(
                        normalizedTokenAddress,
                        ERC20ABI,
                        'decimals',
                        [],
                    ),
                    safeReadContractData(
                        normalizedTokenAddress,
                        ERC20ABI,
                        'allowance',
                        [walletAddress, contractAddress],
                    ),
                    safeReadContractData(
                        normalizedTokenAddress,
                        ERC20ABI,
                        'balanceOf',
                        [walletAddress],
                    ),
                ])
                : [null, null, null, null, null];

        setAdminAddress(disputeAdmin || contractOwner || '');
        const reclaimDelay = Number(reclaimDelayRaw || 0);
        if (reclaimDelay > 0) {
            setAcceptReclaimDelaySeconds(reclaimDelay);
        }
        const nextTokenInfo = {
            address: hasValidTokenAddress ? normalizedTokenAddress : '',
            name: name || 'Payment Token',
            symbol: symbol || 'TOKEN',
            decimals: Number(decimalsValue ?? 6),
            allowance: allowance ?? 0n,
            balance: balance ?? 0n,
        };
        setTokenInfo(nextTokenInfo);

        return nextTokenInfo;
    };

    /** Lightweight refresh for create/fund — avoids admin/metadata reads that burn RPC quota. */
    const refreshTokenBalanceForFunding = async () => {
        if (
            !contractConfigured ||
            !walletAddress ||
            chainId !== targetChainId
        ) {
            return tokenInfo;
        }

        let tokenAddress = tokenInfo.address;
        let decimals = tokenInfo.decimals;
        let symbol = tokenInfo.symbol;
        let name = tokenInfo.name;

        if (!ethers.isAddress(tokenAddress)) {
            tokenAddress = await resolveJobsPaymentToken(contractAddress);
            tokenAddress =
                typeof tokenAddress === 'string' ? tokenAddress.trim() : '';
            if (!ethers.isAddress(tokenAddress)) {
                return tokenInfo;
            }
            const [tokenName, tokenSymbol, decimalsValue] = await Promise.all([
                safeReadContractData(tokenAddress, ERC20ABI, 'name', []),
                safeReadContractData(tokenAddress, ERC20ABI, 'symbol', []),
                safeReadContractData(tokenAddress, ERC20ABI, 'decimals', []),
            ]);
            name = tokenName || name;
            symbol = tokenSymbol || symbol;
            decimals = Number(decimalsValue ?? decimals ?? 6);
        }

        const [allowance, balance] = await Promise.all([
            safeReadContractData(tokenAddress, ERC20ABI, 'allowance', [
                walletAddress,
                contractAddress,
            ]),
            safeReadContractData(tokenAddress, ERC20ABI, 'balanceOf', [
                walletAddress,
            ]),
        ]);

        const nextTokenInfo = {
            address: tokenAddress,
            name: name || 'Payment Token',
            symbol: symbol || 'TOKEN',
            decimals: Number(decimals ?? 6),
            allowance: allowance ?? 0n,
            balance: balance ?? 0n,
        };
        setTokenInfo(nextTokenInfo);
        return nextTokenInfo;
    };

    const mergeTaskInLists = useCallback((hydratedTask) => {
        if (!hydratedTask?.id) return;
        const id = String(hydratedTask.id);
        const merge = (list) =>
            list.map((row) => (String(row.id) === id ? hydratedTask : row));
        setCompanyTasks((prev) => merge(prev));
        setEmployeeTasks((prev) => merge(prev));
    }, []);

    const decryptTasksForViewer = useCallback(
        async (tasks) => {
            if (!walletAddress || !signMessageAsync || !Array.isArray(tasks)) {
                return tasks || [];
            }
            try {
                return await decryptTasksInList(
                    tasks,
                    signMessageAsync,
                    walletAddress,
                    { resolveAttachmentUrlFn: resolveAttachmentUrl },
                );
            } catch (_error) {
                return tasks;
            }
        },
        [walletAddress, signMessageAsync],
    );

    const ensureViewerEncryptionKeyRegistered = useCallback(
        async (token = backendToken) => {
            if (!token || !walletAddress || !signMessageAsync) {
                return;
            }
            try {
                const keyPair = await deriveEncryptionKeyPair(
                    signMessageAsync,
                    walletAddress,
                );
                await backendApi.registerEncryptionPublicKey(
                    token,
                    keyPair.publicKeyBase64,
                );
            } catch (_error) {
                // User may reject the encryption signature; task create/decrypt will prompt again.
            }
        },
        [backendToken, walletAddress, signMessageAsync],
    );

    const buildEncryptedTaskApiPayload = useCallback(
        async (title, description, employeeRecord) =>
            prepareEncryptedTaskCreatePayload({
                signMessageAsync,
                companyWalletAddress: walletAddress,
                employeeWalletAddress: employeeRecord.wallet,
                title,
                description,
                registerEncryptionPublicKey: (encryptionPublicKey) =>
                    backendApi.registerEncryptionPublicKey(
                        backendToken,
                        encryptionPublicKey,
                    ),
                fetchEncryptionPublicKey: (employeeWallet) =>
                    backendApi.getEncryptionPublicKey(backendToken, employeeWallet),
            }),
        [backendToken, signMessageAsync, walletAddress],
    );

    const refreshSelectedTask = useCallback(
        async (token = backendToken) => {
            if (!selectedTaskId || !token) return null;
            try {
                let task = await backendApi.getTaskById(token, selectedTaskId);
                task = await hydrateTaskWithChainData(task);
                task = await decryptTaskPayload(task, signMessageAsync, walletAddress, {
                    resolveAttachmentUrlFn: resolveAttachmentUrl,
                });
                mergeTaskInLists(task);
                return task;
            } catch (error) {
                if (!isJwtExpiredError(error)) {
                    throw error;
                }
                const renewedToken = await createFreshSessionToken();
                return refreshSelectedTask(renewedToken);
            }
        },
        [
            backendToken,
            selectedTaskId,
            hydrateTaskWithChainData,
            mergeTaskInLists,
            createFreshSessionToken,
            signMessageAsync,
            walletAddress,
        ],
    );

    const hydrateTaskForDisplay = useCallback(
        async (task) => {
            const hydrated = await hydrateTaskWithChainData(task);
            if (
                !signMessageAsync ||
                !walletAddress ||
                !taskNeedsClientDecrypt(hydrated)
            ) {
                return hydrated;
            }
            try {
                return await decryptTaskPayload(hydrated, signMessageAsync, walletAddress, {
                    resolveAttachmentUrlFn: resolveAttachmentUrl,
                });
            } catch (_error) {
                return hydrated;
            }
        },
        [hydrateTaskWithChainData, signMessageAsync, walletAddress],
    );

    const refreshBackendState = async (
        token,
        { silent = false, rethrow = false, skipLoading = false } = {},
    ) => {
        if (!token) return;

        if (!skipLoading) {
            setLoading(true);
        }
        try {
            await refreshTokenState();

            const [
                companyResult,
                employeeCompanyResult,
                employeeProfileResult,
                employeeTaskResult,
                notificationResult,
            ] = await Promise.allSettled([
                backendApi.getMyCompany(token),
                backendApi.getMyEmployeeCompany(token),
                backendApi.getMyEmployeeProfile(token),
                backendApi.getEmployeeTasks(token, { fetchAll: true, scope: 'all' }),
                backendApi.getNotifications(token),
            ]);

            const nextCompany =
                companyResult.status === 'fulfilled'
                    ? companyResult.value
                    : null;
            const nextEmployeeCompany =
                employeeCompanyResult.status === 'fulfilled'
                    ? employeeCompanyResult.value
                    : null;
            const nextEmployeeProfile =
                employeeProfileResult.status === 'fulfilled'
                    ? employeeProfileResult.value
                    : null;
            const employeeTaskRows =
                employeeTaskResult.status === 'fulfilled'
                    ? employeeTaskResult.value
                    : [];
            const notificationRows =
                notificationResult.status === 'fulfilled'
                    ? notificationResult.value
                    : [];
            const employeeRows = nextCompany
                ? await backendApi.listEmployees(token)
                : [];
            const companyTaskRows = nextCompany
                ? await backendApi.getCompanyTasks(token, { fetchAll: true, scope: 'all' })
                : [];

            setCompany(nextCompany);
            setEmployeeCompany(nextEmployeeCompany);
            setMyEmployeeRecord(nextEmployeeProfile);
            setEmployees(employeeRows);
            const hydratedCompanyTasks = await Promise.all(
                companyTaskRows.map((task) => hydrateTaskWithChainData(task)),
            );
            const hydratedEmployeeTasks = await Promise.all(
                employeeTaskRows.map((task) => hydrateTaskWithChainData(task)),
            );
            // Unlock encryption once before decrypting lists / paginated hydrators.
            await ensureViewerEncryptionKeyRegistered(token);

            setCompanyTasks(await decryptTasksForViewer(hydratedCompanyTasks));
            setEmployeeTasks(await decryptTasksForViewer(hydratedEmployeeTasks));
            setNotifications(notificationRows);
            setTaskListRefreshKey((current) => current + 1);

            if (nextCompany) {
                setCompanyForm(companyToForm(nextCompany));
            }
        } catch (error) {
            if (!silent) {
                fireErrorToast(getReadableError(error));
            }
            if (rethrow) {
                throw error;
            }
        } finally {
            if (!skipLoading) {
                setLoading(false);
            }
        }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!isConnected || !walletAddress || chainId !== targetChainId) {
            clearEncryptionKeyPairCache(walletAddress);
            setBackendToken('');
            setCompany(null);
            setEmployeeCompany(null);
            setEmployees([]);
            setCompanyTasks([]);
            setEmployeeTasks([]);
            setNotifications([]);
            setJoinChoice(null);
            setCompanyModalOpen(false);
            return;
        }

        let cancelled = false;

        const bootstrap = async () => {
            try {
                setAuthLoading(true);
                const cachedToken = getStoredBackendToken(walletAddress);

                if (cachedToken && !cancelled) {
                    setBackendToken(cachedToken);
                    try {
                        await refreshBackendState(cachedToken, {
                            silent: true,
                            rethrow: true,
                        });
                    } catch (error) {
                        if (!isJwtExpiredError(error)) {
                            throw error;
                        }
                        const renewedToken = await createFreshSessionToken();
                        if (cancelled) return;
                        await refreshBackendState(renewedToken, {
                            silent: true,
                            rethrow: true,
                        });
                    }
                    return;
                }

                const renewedToken = await createFreshSessionToken();
                if (cancelled) return;

                await refreshBackendState(renewedToken, {
                    silent: true,
                    rethrow: true,
                });
            } catch (error) {
                if (!cancelled) {
                    fireErrorToast(getReadableError(error));
                }
            } finally {
                if (!cancelled) {
                    setAuthLoading(false);
                }
            }
        };

        bootstrap();
        return () => {
            cancelled = true;
        };
    }, [walletAddress, isConnected, chainId]);

    useEffect(() => {
        if (
            taskViews.length &&
            !taskViews.some((view) => view.key === activeTaskView)
        ) {
            setActiveTaskView(taskViews[0].key);
        }
    }, [activeTaskView, taskViews]);

    useEffect(() => {
        if (!hasJoinSelection && activeWorkspaceTab !== 'setup') {
            setActiveWorkspaceTab('setup');
        }
    }, [hasJoinSelection, activeWorkspaceTab]);

    useEffect(() => {
        const section = searchParams.get('section');
        const panel = searchParams.get('panel');
        const taskView = searchParams.get('view');

        if (
            section &&
            [
                'setup',
                'create',
                'workspace',
                'stats',
                'history',
                'earnings',
                'profile',
            ].includes(section)
        ) {
            setActiveWorkspaceTab(section);
        }

        if (section === 'workspace' && taskView) {
            const hasTaskView = taskViews.some((view) => view.key === taskView);
            if (hasTaskView) {
                setActiveTaskView(taskView);
            }
        }
    }, [searchParams, taskViews]);

    useEffect(() => {
        const section = searchParams.get('section') || '';
        const panel = searchParams.get('panel');
        if (section === 'workspace' && (panel === null || panel === '')) {
            setSearchParams(
                (prev) => {
                    const n = new URLSearchParams(prev);
                    n.set('panel', 'tasks');
                    return n;
                },
                { replace: true },
            );
        }
    }, [searchParams, setSearchParams]);

    const workspacePagePanel = useMemo(() => {
        if (activeWorkspaceTab !== 'workspace') return null;
        return searchParams.get('panel') === 'activity' ? 'activity' : 'tasks';
    }, [activeWorkspaceTab, searchParams]);

    useEffect(() => {
        if (!selectedTaskId || !backendToken || selectedTask) {
            setDetailTaskLoading(false);
            return undefined;
        }

        let cancelled = false;
        setDetailTaskLoading(true);

        (async () => {
            try {
                await refreshSelectedTask();
            } catch {
                /* not found stays empty */
            } finally {
                if (!cancelled) setDetailTaskLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        selectedTaskId,
        backendToken,
        selectedTask,
        refreshSelectedTask,
    ]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key !== 'Escape') return;
            if (searchParams.get('taskId')) {
                closeTaskDetail();
                return;
            }
            if (searchParams.get('employeeId')) {
                closeEmployeeDetail();
                return;
            }
            if (searchParams.get('panel') === 'add-employee') {
                closeAddEmployee();
                return;
            }
            if (searchParams.get('panel') === 'edit-employee') {
                closeEditEmployee();
                return;
            }
            setCompanyModalOpen(false);
        };

        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [
        searchParams,
        closeTaskDetail,
        closeEmployeeDetail,
        closeAddEmployee,
        closeEditEmployee,
    ]);

    const refreshAll = async ({ skipLoading = false } = {}) => {
        if (backendToken) {
            try {
                await refreshBackendState(backendToken, {
                    silent: true,
                    rethrow: true,
                    skipLoading,
                });
            } catch (error) {
                if (!isJwtExpiredError(error)) {
                    throw error;
                }

                const renewedToken = await createFreshSessionToken();
                await refreshBackendState(renewedToken, {
                    silent: true,
                    rethrow: true,
                    skipLoading,
                });
            }
        }
    };

    const ensureCompanyOnChainRegistered = async (companyRecord = company) => {
        if (!contractConfigured || !walletAddress || !companyRecord?.id) {
            return null;
        }
        if (String(companyRecord.onChainRegistryStatus || '').toLowerCase() === 'synced') {
            return companyRecord.onChainCompanyRegistryId || null;
        }

        const existingId = await readCompanyRegistryId(contractAddress, walletAddress);
        if (existingId > 0) {
            await backendApi.updateCompanyOnChainRegistry(backendToken, {
                companyRegistryId: String(existingId),
                onChainRegistryStatus: 'synced',
            });
            return String(existingId);
        }

        const { txHash, registryId } = await registerCompanyOnChain({
            writeContractAsync,
            contractAddress,
            walletAddress,
            chainId: targetChainId,
            companyId: companyRecord.id,
        });
        await backendApi.updateCompanyOnChainRegistry(backendToken, {
            companyRegistryId: registryId,
            txHash,
            onChainRegistryStatus: 'synced',
        });
        return registryId;
    };

    const ensureEmployeeOnChainRegistered = async (employeeRecord) => {
        if (!contractConfigured || !employeeRecord?.wallet) {
            return null;
        }
        if (String(employeeRecord.onChainRegistryStatus || '').toLowerCase() === 'synced') {
            return employeeRecord.onChainEmployeeRegistryId || null;
        }

        await ensureCompanyOnChainRegistered();

        const employeeWallet = employeeRecord.wallet;
        const existingId = await readEmployeeRegistryId(contractAddress, employeeWallet);
        if (existingId > 0) {
            if (employeeRecord.id) {
                await backendApi.updateEmployeeOnChainRegistry(
                    backendToken,
                    employeeRecord.id,
                    {
                        employeeRegistryId: String(existingId),
                        employmentClass: employeeRecord.employmentType,
                        onChainRegistryStatus: 'synced',
                    },
                );
            }
            return String(existingId);
        }

        if (!employeeRecord.id) {
            throw new Error(
                'Employee record is incomplete. Refresh the page and try again.',
            );
        }

        const { txHash, registryId } = await registerEmployeeOnChain({
            writeContractAsync,
            contractAddress,
            chainId: targetChainId,
            employeeWallet,
            employmentType: employeeRecord.employmentType,
        });
        await backendApi.updateEmployeeOnChainRegistry(backendToken, employeeRecord.id, {
            employeeRegistryId: registryId,
            employmentClass: employeeRecord.employmentType,
            txHash,
            onChainRegistryStatus: 'synced',
        });
        return registryId;
    };

    const resolveTaskEmployeeRecord = (task, employeeWallet) => {
        if (task?.employee?.wallet) {
            return task.employee;
        }
        if (task?.employeeId) {
            const matched = employees.find(
                (employee) => String(employee.id) === String(task.employeeId),
            );
            if (matched) {
                return matched;
            }
        }
        if (employeeWallet) {
            return { wallet: employeeWallet, employmentType: 'freelance' };
        }
        return null;
    };

    const fundTaskEscrowOnChain = async ({
        taskId,
        amount,
        deadline,
        employeeRecord,
        employeeWallet,
        tokenSnapshot = tokenInfo,
    }) => {
        if (!contractConfigured) {
            throw new Error('Task escrow contract address is not configured.');
        }

        const resolvedEmployee =
            employeeRecord ||
            (employeeWallet
                ? { wallet: employeeWallet, employmentType: 'freelance' }
                : null);
        const resolvedEmployeeWallet =
            employeeWallet || resolvedEmployee?.wallet;

        if (!taskId) {
            throw new Error('Task id is required to fund escrow.');
        }
        if (!ethers.isAddress(resolvedEmployeeWallet)) {
            throw new Error('Employee wallet is invalid.');
        }

        await ensureCompanyOnChainRegistered();
        await ensureEmployeeOnChainRegistered(resolvedEmployee);

        const taskAmount = ethers.parseUnits(
            String(amount || '0'),
            tokenSnapshot.decimals,
        );
        const dueDateTimestamp = deadline
            ? Math.floor(new Date(deadline).getTime() / 1000)
            : 0;

        if (taskAmount <= 0n) throw new Error('Task amount is invalid.');
        if (tokenSnapshot.balance < taskAmount) {
            throw new Error(
                `Wallet balance is lower than the requested ${tokenSnapshot.symbol} amount.`,
            );
        }

        await ensureExactTokenAllowance({
            writeContractAsync,
            tokenAddress: tokenSnapshot.address,
            spender: contractAddress,
            owner: walletAddress,
            requiredAmount: taskAmount,
            chainId: targetChainId,
        });

        const backendReference = `backend://task/${taskId}`;
        const metadataHash = ethers.keccak256(
            ethers.toUtf8Bytes(
                JSON.stringify({
                    taskId,
                    employeeWallet: resolvedEmployeeWallet,
                    amount: String(amount),
                }),
            ),
        );

        // Hardcoded gas must stay above mainnet estimate (~468k observed).
        // Too-low limits cause status=0 with ~98% gas used (looks like a silent revert).
        const txHash = await writeContractAsync({
            address: contractAddress,
            abi: TaskEscrowABI,
            functionName: JOB_ESCROW_FN.createJobEscrow,
            args: [
                tokenSnapshot.address,
                resolvedEmployeeWallet,
                taskAmount,
                backendReference,
                metadataHash,
                dueDateTimestamp,
            ],
            chainId: targetChainId,
            gas: 750000n,
        });

        const receipt = await waitForSuccessfulTransactionReceipt(txHash);
        const escrowId = extractEscrowIdFromReceipt(receipt);
        if (!escrowId) {
            throw new Error(
                'Escrow transaction succeeded but escrow id could not be parsed.',
            );
        }

        return { txHash, escrowId };
    };

    const createSalariedTaskAssignmentOnChain = async ({
        taskId,
        deadline,
        employeeRecord,
        employeeWallet,
    }) => {
        if (!contractConfigured) {
            throw new Error('Task escrow contract address is not configured.');
        }

        const resolvedEmployeeWallet =
            employeeWallet || employeeRecord?.wallet;

        if (!taskId) {
            throw new Error('Task id is required to create assignment.');
        }
        if (!ethers.isAddress(resolvedEmployeeWallet)) {
            throw new Error('Employee wallet is invalid.');
        }

        await ensureCompanyOnChainRegistered();
        const registryId = await ensureEmployeeOnChainRegistered(employeeRecord);

        // On-chain class must be Salaried (DB alone is not enough).
        const onChainClass = await readEmployeeEmploymentClass(
            contractAddress,
            resolvedEmployeeWallet,
        );
        if (onChainClass !== EMPLOYMENT_CLASS_ON_CHAIN.salaried) {
            const employeeRegistryId =
                registryId ||
                employeeRecord?.onChainEmployeeRegistryId ||
                (await readEmployeeRegistryId(
                    contractAddress,
                    resolvedEmployeeWallet,
                ));
            if (!employeeRegistryId) {
                throw new Error(
                    'Employee is not Salaried on-chain and has no registry id to update.',
                );
            }
            fireInfoToast('Updating on-chain employment class to Salaried…');
            await updateEmployeeEmploymentClassOnChain({
                writeContractAsync,
                contractAddress,
                chainId: targetChainId,
                employeeRegistryId,
                employmentType: 'salaried',
            });
        }

        let canAssign = await readCanAssignPayrollTask(
            contractAddress,
            walletAddress,
            resolvedEmployeeWallet,
        );

        // Auto enroll/fund payroll if missing — createJobAssignment reverts without it.
        if (!canAssign) {
            const enrollmentId = await readPayrollEnrollmentId(
                contractAddress,
                walletAddress,
                resolvedEmployeeWallet,
            );
            fireInfoToast(
                enrollmentId
                    ? 'Funding payroll vault before assigning task…'
                    : 'Enrolling & funding payroll before assigning task…',
            );

            if (!enrollmentId) {
                await enrollEmployeePayrollOnChain(
                    employeeRecord,
                    employeeRecord?.monthlySalary,
                );
            } else {
                await fundEmployeePayrollOnChain(
                    employeeRecord,
                    employeeRecord?.monthlySalary,
                );
            }

            canAssign = await readCanAssignPayrollTask(
                contractAddress,
                walletAddress,
                resolvedEmployeeWallet,
            );
        }

        if (!canAssign) {
            const reason = await diagnoseSalariedAssignmentBlockers({
                contractAddress,
                companyWallet: walletAddress,
                employeeWallet: resolvedEmployeeWallet,
            });
            throw new Error(
                reason ||
                    'Payroll vault must be funded before assigning tasks to salaried employees.',
            );
        }

        const dueDateTimestamp = deadline
            ? Math.floor(new Date(deadline).getTime() / 1000)
            : 0;

        const backendReference = `backend://task/${taskId}`;
        const metadataHash = ethers.keccak256(
            ethers.toUtf8Bytes(
                JSON.stringify({
                    taskId,
                    employeeWallet: resolvedEmployeeWallet,
                    payrollAssignment: true,
                }),
            ),
        );

        const { txHash } = await createJobAssignmentOnChain({
            writeContractAsync,
            contractAddress,
            chainId: targetChainId,
            employeeWallet: resolvedEmployeeWallet,
            metadataURI: backendReference,
            metadataHash,
            dueDateTimestamp,
        });

        const receipt = await waitForSuccessfulTransactionReceipt(txHash);
        const escrowId = extractEscrowIdFromReceipt(receipt);
        if (!escrowId) {
            throw new Error(
                'Assignment transaction succeeded but assignment id could not be parsed.',
            );
        }

        return { txHash, escrowId };
    };

    const enrollEmployeePayrollOnChain = async (employeeRecord, monthlySalary) => {
        if (!contractConfigured || !walletAddress) {
            throw new Error('Contract or wallet is not configured.');
        }
        const salaryRaw = String(monthlySalary || employeeRecord?.monthlySalary || '').trim();
        if (!salaryRaw || Number(salaryRaw) <= 0) {
            throw new Error('Monthly salary is required to enroll payroll.');
        }

        await ensureCompanyOnChainRegistered();
        await ensureEmployeeOnChainRegistered(employeeRecord);

        const tokenSnapshot = (await refreshTokenState()) || tokenInfo;
        const monthlyAmount = ethers.parseUnits(salaryRaw, tokenSnapshot.decimals);

        await ensureExactTokenAllowance({
            writeContractAsync,
            tokenAddress: tokenSnapshot.address,
            spender: contractAddress,
            owner: walletAddress,
            requiredAmount: monthlyAmount,
            chainId: targetChainId,
        });

        return enrollAndFundPayrollOnChain({
            writeContractAsync,
            contractAddress,
            chainId: targetChainId,
            companyWallet: walletAddress,
            employeeWallet: employeeRecord.wallet,
            tokenAddress: tokenSnapshot.address,
            amountPerPeriod: monthlyAmount,
            periodSeconds: 0,
        });
    };

    const fundEmployeePayrollOnChain = async (employeeRecord, amountOverride) => {
        if (!contractConfigured || !walletAddress) {
            throw new Error('Contract or wallet is not configured.');
        }

        const salaryRaw = String(
            amountOverride || employeeRecord?.monthlySalary || '',
        ).trim();
        if (!salaryRaw || Number(salaryRaw) <= 0) {
            throw new Error('Monthly salary amount is required to fund payroll.');
        }

        const tokenSnapshot = (await refreshTokenState()) || tokenInfo;
        const depositAmount = ethers.parseUnits(salaryRaw, tokenSnapshot.decimals);

        await ensureExactTokenAllowance({
            writeContractAsync,
            tokenAddress: tokenSnapshot.address,
            spender: contractAddress,
            owner: walletAddress,
            requiredAmount: depositAmount,
            chainId: targetChainId,
        });

        return depositPayrollOnChain({
            writeContractAsync,
            contractAddress,
            chainId: targetChainId,
            tokenAddress: tokenSnapshot.address,
            amount: depositAmount,
        });
    };

    const createEscrowForTask = async ({
        task,
        employeeRecord,
        employeeWallet,
        tokenSnapshot = tokenInfo,
    }) => {
        const resolvedEmployee =
            employeeRecord ||
            resolveTaskEmployeeRecord(task, employeeWallet);

        const { txHash, escrowId } = await fundTaskEscrowOnChain({
            taskId: task.id,
            amount: task.amount,
            deadline: task.deadline,
            employeeRecord: resolvedEmployee,
            employeeWallet,
            tokenSnapshot,
        });

        await backendApi.recordEscrowCreated(backendToken, {
            taskId: task.id,
            escrowId,
            txHash,
            contractAddress,
            chainId: String(chainId),
        });

        return { txHash, escrowId };
    };

    const assertEscrowAcceptedOnChain = async (escrowId) => {
        const onChain = await safeReadContractData(
            contractAddress,
            TaskEscrowABI,
            'escrows',
            [Number(escrowId)],
        );
        if (isEscrowAcceptedOnChain({ onChain }) !== true) {
            throw new Error(
                'Escrow acceptance was not confirmed on-chain. Refresh and try again.',
            );
        }
    };

    const acceptEscrowOnChainForTask = async (task) => {
        if (!contractConfigured) {
            throw new Error('Task escrow contract address is not configured.');
        }
        if (!task?.escrowId) {
            throw new Error(
                'Company must fund escrow before you can accept this task.',
            );
        }

        const escrowId = Number(task.escrowId);
        if (!Number.isFinite(escrowId) || escrowId <= 0) {
            throw new Error('Task escrow id is invalid.');
        }

        const alreadyAccepted = isEscrowAcceptedOnChain(task);
        if (alreadyAccepted === true) {
            return task.acceptTxHash || task.txHash || null;
        }

        await publicClient.simulateContract({
            account: walletAddress,
            address: contractAddress,
            abi: TaskEscrowABI,
            functionName: JOB_ESCROW_FN.acceptJobEscrow,
            args: [escrowId],
            chainId: targetChainId,
        });

        const txHash = await writeContractAsync({
            address: contractAddress,
            abi: TaskEscrowABI,
            functionName: JOB_ESCROW_FN.acceptJobEscrow,
            args: [escrowId],
            chainId: targetChainId,
        });
        await waitForSuccessfulTransactionReceipt(txHash);
        await assertEscrowAcceptedOnChain(task.escrowId);
        return txHash;
    };

    const syncAcceptedTaskToBackend = async (task, acceptTxHash) => {
        if (resolveTaskStatus(task) !== 'pending') {
            return null;
        }
        return backendApi.acceptTask(backendToken, task.id, {
            acceptTxHash,
        });
    };

    const handleEmployeeAcceptTask = async (
        task,
        {
            successMessage = 'Task accepted successfully.',
            actionKey = `task-${task?.id}-accept-task`,
        } = {},
    ) => {
        if (acceptTaskInFlightRef.current) {
            return;
        }
        if (!task?.escrowId) {
            fireInfoToast(
                'Wait for your company to fund escrow before accepting this task.',
            );
            return;
        }

        acceptTaskInFlightRef.current = true;

        try {
            setActiveAction(actionKey);
            const acceptTxHash = await acceptEscrowOnChainForTask(task);
            await syncAcceptedTaskToBackend(task, acceptTxHash);
            fireSuccessToast(successMessage);
            closeTaskDetail();
            await refreshAll();
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            acceptTaskInFlightRef.current = false;
            setActiveAction('');
        }
    };

    const submitTaskWorkOnChain = async ({
        task,
        message,
        attachments = [],
        encryptionCommitment = null,
    }) => {
        if (!task?.escrowId) {
            return null;
        }
        if (!contractConfigured) {
            throw new Error('Task escrow contract address is not configured.');
        }

        const escrowId = Number(task.escrowId);
        if (Number(task?.onChainWork?.workStatus ?? 0) === 2) {
            return null;
        }

        const submissionURI = `backend://task/${task.id}/submission/${crypto.randomUUID()}`;
        const submissionHash = ethers.keccak256(
            ethers.toUtf8Bytes(
                JSON.stringify({
                    taskId: task.id,
                    ...(encryptionCommitment
                        ? {
                              encryptionVersion: 'v1',
                              commitment: encryptionCommitment,
                          }
                        : {
                              message,
                              attachments,
                          }),
                    submittedBy: walletAddress,
                }),
            ),
        );

        const txHash = await writeContractAsync({
            address: contractAddress,
            abi: TaskEscrowABI,
            functionName: JOB_ESCROW_FN.submitJobWork,
            args: [escrowId, submissionURI, submissionHash],
            chainId: targetChainId,
            gas: 500000n,
        });
        await waitForSuccessfulTransactionReceipt(txHash);

        return { txHash, submissionURI, submissionHash };
    };

    const handleCompanySubmit = async (event) => {
        event.preventDefault();
        if (!backendToken) return;

        try {
            setSubmitting(true);
            const payload = buildCompanyPayload(companyForm);
            if (!payload.name) {
                fireInfoToast('Enter a company name first.');
                return;
            }

            if (company) {
                const needsOnChainSync =
                    contractConfigured &&
                    String(company.onChainRegistryStatus || '').toLowerCase() !==
                        'synced';

                if (needsOnChainSync) {
                    await ensureCompanyOnChainRegistered(company);
                    await backendApi.updateCompany(backendToken, payload);
                    fireSuccessToast('Company updated and registered on-chain.');
                } else {
                    await backendApi.updateCompany(backendToken, payload);
                    fireSuccessToast('Company updated successfully.');
                    if (contractConfigured) {
                        try {
                            await ensureCompanyOnChainRegistered(company);
                        } catch (registryError) {
                            fireErrorToast(getReadableError(registryError));
                        }
                    }
                }
            } else {
                if (!contractConfigured) {
                    fireErrorToast(
                        'Task escrow contract is not configured for this environment.',
                    );
                    return;
                }

                const draftCompanyId = crypto.randomUUID();
                let companyRegistryId = null;
                let registryTxHash = null;

                const existingOnChainId = await readCompanyRegistryId(
                    contractAddress,
                    walletAddress,
                );
                if (existingOnChainId > 0) {
                    companyRegistryId = String(existingOnChainId);
                } else {
                    const registered = await registerCompanyOnChain({
                        writeContractAsync,
                        contractAddress,
                        walletAddress,
                        chainId: targetChainId,
                        companyId: draftCompanyId,
                    });
                    companyRegistryId = registered.registryId;
                    registryTxHash = registered.txHash;
                }

                await backendApi.createCompany(backendToken, {
                    ...payload,
                    id: draftCompanyId,
                    companyRegistryId,
                    ...(registryTxHash ? { txHash: registryTxHash } : {}),
                    onChainRegistryStatus: 'synced',
                });
                fireSuccessToast('Company created and registered on-chain.');
            }

            await refreshAll();
            setCompanyModalOpen(false);
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEmployeeSubmit = async (event) => {
        event.preventDefault();
        if (!backendToken) return;

        try {
            setSubmitting(true);
            const payload = buildEmployeePayload(employeeForm, {
                includeWallet: true,
            });
            if (!payload.name || !payload.wallet) {
                fireInfoToast('Employee name and wallet are required.');
                return;
            }
            const salaryError = validateSalariedMonthlySalary(payload);
            if (salaryError) {
                fireInfoToast(salaryError);
                return;
            }

            let onChainRegistry = null;
            if (contractConfigured) {
                const companySynced =
                    String(company?.onChainRegistryStatus || '').toLowerCase() ===
                    'synced';
                if (!companySynced) {
                    await ensureCompanyOnChainRegistered();
                }

                const existingOnChainId = await readEmployeeRegistryId(
                    contractAddress,
                    payload.wallet,
                );
                if (existingOnChainId > 0) {
                    onChainRegistry = {
                        registryId: String(existingOnChainId),
                        txHash: undefined,
                    };
                } else {
                    onChainRegistry = await registerEmployeeOnChain({
                        writeContractAsync,
                        contractAddress,
                        chainId: targetChainId,
                        employeeWallet: payload.wallet,
                        employmentType: payload.employmentType,
                    });
                }
            }

            let payrollRegistry = null;
            if (
                contractConfigured &&
                String(payload.employmentType || '').toLowerCase() === 'salaried' &&
                payload.monthlySalary
            ) {
                try {
                    payrollRegistry = await enrollEmployeePayrollOnChain(
                        {
                            wallet: payload.wallet,
                            employmentType: payload.employmentType,
                            monthlySalary: payload.monthlySalary,
                        },
                        payload.monthlySalary,
                    );
                    fireSuccessToast('Payroll enrolled and first month funded on-chain.');
                } catch (payrollError) {
                    const msg = getReadableError(payrollError);
                    fireErrorToast(
                        `${msg} You can still save the employee, then use Fund payroll before assigning tasks.`,
                    );
                }
            }

            await backendApi.addEmployee(backendToken, {
                ...payload,
                ...(onChainRegistry
                    ? {
                          onChainEmployeeRegistryId: onChainRegistry.registryId,
                          ...(onChainRegistry.txHash
                              ? { onChainRegistryTxHash: onChainRegistry.txHash }
                              : {}),
                          onChainRegistryStatus: 'synced',
                          onChainEmploymentClass: payload.employmentType,
                      }
                    : {}),
                ...(payrollRegistry
                    ? {
                          onChainPayrollEnrollmentId: payrollRegistry.enrollmentId,
                          onChainPayrollTxHash: payrollRegistry.txHash,
                          onChainPayrollStatus: 'synced',
                      }
                    : {}),
            });
            setEmployeeForm(emptyEmployeeForm);
            fireSuccessToast(
                onChainRegistry
                    ? 'Employee added and registered on-chain.'
                    : 'Employee added successfully.',
            );
            await refreshAll();
            closeAddEmployee();
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateEmployeeSubmit = async (event) => {
        event.preventDefault();
        if (!backendToken || !selectedEmployee || !isCompanyOwner) return;
        if (String(selectedEmployee.status || '').toLowerCase() === 'removed') {
            fireInfoToast('Removed employees cannot be edited.');
            return;
        }

        try {
            setSubmitting(true);
            const payload = buildEmployeePayload(editEmployeeForm);
            if (!payload.name) {
                fireInfoToast('Employee name is required.');
                return;
            }
            const salaryError = validateSalariedMonthlySalary(payload);
            if (salaryError) {
                fireInfoToast(salaryError);
                return;
            }
            payload.status = editEmployeeForm.status;
            await backendApi.updateEmployee(
                backendToken,
                selectedEmployee.id,
                payload,
            );
            fireSuccessToast('Employee updated successfully.');
            navigateToEmployeeDetailAfterEdit(selectedEmployee.id);
            await refreshAll();
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            setSubmitting(false);
        }
    };

    const closeEmployeeSelfProfileModal = useCallback(() => {
        employeeSelfProfilePromptRef.current.suppressed = true;
        setEmployeeSelfModalOpen(false);
    }, []);

    const handleEmployeeSelfSubmit = async (event) => {
        event.preventDefault();
        if (!backendToken) return;

        try {
            setSubmitting(true);
            const payload = buildEmployeeSelfPayload(employeeSelfForm);
            if (!payload.name) {
                fireInfoToast('Enter your name to continue.');
                return;
            }
            const updated = await backendApi.updateMyEmployeeProfile(
                backendToken,
                payload,
            );
            employeeSelfProfilePromptRef.current.suppressed = true;
            setEmployeeSelfModalOpen(false);
            if (updated) {
                setMyEmployeeRecord((current) => ({
                    ...(current || {}),
                    ...updated,
                    profileCompleted: true,
                }));
            }
            fireSuccessToast('Profile saved.');
            await refreshAll();
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (!myEmployeeRecord) {
            employeeSelfProfilePromptRef.current = {
                suppressed: false,
                opened: false,
            };
            return;
        }

        const prompt = employeeSelfProfilePromptRef.current;
        const profileDone =
            myEmployeeRecord.profileCompleted === true ||
            myEmployeeRecord.profileCompleted === 1;

        if (
            prompt.suppressed ||
            profileDone ||
            effectiveJoinMode !== 'employee'
        ) {
            return;
        }

        if (prompt.opened) return;

        prompt.opened = true;
        setEmployeeSelfForm(employeeToSelfForm(myEmployeeRecord));
        setEmployeeSelfModalOpen(true);
    }, [myEmployeeRecord, effectiveJoinMode]);

    const handleRemoveEmployee = async (employee) => {
        if (!backendToken || !isCompanyOwner) return;
        if (String(employee.status || '').toLowerCase() === 'removed') return;
        if (
            !window.confirm(
                `Remove ${employee.name || 'this employee'} from the team? They will be marked as removed.`,
            )
        ) {
            return;
        }

        try {
            setSubmitting(true);
            await backendApi.removeEmployee(backendToken, employee.id);
            fireSuccessToast('Employee removed successfully.');
            if (String(taskForm.employeeId) === String(employee.id)) {
                setTaskForm((current) => ({ ...current, employeeId: '' }));
            }
            closeEditEmployee({ forceList: true });
            await refreshAll();
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            setSubmitting(false);
        }
    };

    const handleFundEmployeePayroll = async (employee) => {
        if (!backendToken || !isCompanyOwner || !employee) return;
        if (!isSalariedEmployee(employee)) return;

        try {
            setPayrollFundingId(employee.id);
            if (String(employee.onChainPayrollStatus || '').toLowerCase() !== 'synced') {
                const payrollRegistry = await enrollEmployeePayrollOnChain(
                    employee,
                    employee.monthlySalary,
                );
                await backendApi.updateEmployee(backendToken, employee.id, {
                    onChainPayrollEnrollmentId: payrollRegistry.enrollmentId,
                    onChainPayrollTxHash: payrollRegistry.txHash,
                    onChainPayrollStatus: 'synced',
                });
                fireSuccessToast('Payroll enrolled and funded on-chain.');
            } else {
                await fundEmployeePayrollOnChain(employee);
                fireSuccessToast('Monthly payroll funded on-chain.');
            }
            await refreshAll();
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            setPayrollFundingId(null);
        }
    };

    const refreshEmployeePayrollStatus = useCallback(async () => {
        if (
            effectiveJoinMode !== 'employee' ||
            !contractConfigured ||
            !walletAddress ||
            !employeeCompany?.ownerWallet
        ) {
            setEmployeePayrollStatus(null);
            return null;
        }

        const profile = myEmployeeRecord || employeeProfile;
        // Prefer on-chain status; only skip when DB clearly says freelance.
        if (
            profile &&
            String(profile.employmentType || '').toLowerCase() === 'freelance'
        ) {
            setEmployeePayrollStatus(null);
            return null;
        }

        setEmployeePayrollLoading(true);
        try {
            const status = await loadEmployeePayrollStatus({
                contractAddress,
                companyWallet: employeeCompany.ownerWallet,
                employeeWallet: walletAddress,
                tokenDecimals: tokenInfo.decimals || 6,
            });
            setEmployeePayrollStatus(status);
            return status;
        } catch (_error) {
            setEmployeePayrollStatus(null);
            return null;
        } finally {
            setEmployeePayrollLoading(false);
        }
    }, [
        effectiveJoinMode,
        contractConfigured,
        walletAddress,
        employeeCompany?.ownerWallet,
        myEmployeeRecord,
        employeeProfile,
        contractAddress,
        tokenInfo.decimals,
    ]);

    useEffect(() => {
        refreshEmployeePayrollStatus();
    }, [refreshEmployeePayrollStatus]);

    const handleClaimPayroll = async () => {
        if (!employeePayrollStatus?.enrollmentId) {
            fireInfoToast('No payroll enrollment found for your wallet.');
            return;
        }
        if (!employeePayrollStatus.claimable) {
            fireInfoToast('Salary is not claimable yet.');
            return;
        }

        try {
            setEmployeePayrollClaiming(true);
            await claimPayrollOnChain({
                writeContractAsync,
                contractAddress,
                chainId: targetChainId,
                enrollmentId: employeePayrollStatus.enrollmentId,
            });
            fireSuccessToast(
                `Salary collected: ${employeePayrollStatus.amountFormatted} ${tokenInfo.symbol}.`,
            );
            await refreshEmployeePayrollStatus();
            await refreshAll({ skipLoading: true });
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            setEmployeePayrollClaiming(false);
        }
    };

    const handleToggleEmployeeStatus = async (employee) => {
        if (!backendToken || !isCompanyOwner) return;
        const current = String(employee.status || 'active').toLowerCase();
        if (current === 'removed') return;
        const next = current === 'active' ? 'inactive' : 'active';

        try {
            setEmployeeStatusUpdatingId(employee.id);
            await backendApi.updateEmployee(backendToken, employee.id, { status: next });
            fireSuccessToast(
                next === 'active' ? 'Employee activated.' : 'Employee deactivated.',
            );
            await refreshAll();
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            setEmployeeStatusUpdatingId(null);
        }
    };

    const handleCreateTaskSubmit = async (event) => {
        event.preventDefault();
        if (createTaskInFlightRef.current) {
            return;
        }

        setTaskFormSubmitError('');
        setTaskFormErrors({});

        if (!isCompanyOwner) {
            setTaskFormSubmitError('Only the company owner can create tasks.');
            return;
        }
        if (!backendToken) {
            setTaskFormSubmitError(
                'Connect your wallet and sign in before creating a task.',
            );
            return;
        }
        if (!contractConfigured) {
            setTaskFormSubmitError(
                'Task escrow contract is not configured for this environment.',
            );
            return;
        }

        const selectedEmployee = employees.find(
            (employee) =>
                String(employee.id) === String(taskForm.employeeId),
        );

        const validationErrors = validateCreateTaskForm(taskForm, {
            activeEmployeeCount,
            selectedEmployee,
        });
        if (hasCreateTaskFormErrors(validationErrors)) {
            setTaskFormErrors(validationErrors);
            return;
        }

        if (!selectedEmployee) {
            setTaskFormErrors({
                employeeId: 'Selected employee is no longer available.',
            });
            return;
        }

        const salariedTask = isSalariedEmployee(selectedEmployee);

        createTaskInFlightRef.current = true;

        try {
            setSubmitting(true);

            const draftTaskId = crypto.randomUUID();
            const deadlineIso = taskForm.dueDate
                ? new Date(taskForm.dueDate).toISOString()
                : null;
            const plainTitle = taskForm.title.trim();
            const plainDescription = buildTaskDescription(taskForm);
            const encryptedFields = await buildEncryptedTaskApiPayload(
                plainTitle,
                plainDescription,
                selectedEmployee,
            );

            if (salariedTask) {
                const { escrowId, txHash } = await createSalariedTaskAssignmentOnChain({
                    taskId: draftTaskId,
                    deadline: deadlineIso,
                    employeeRecord: selectedEmployee,
                });

                await backendApi.createTask(backendToken, {
                    id: draftTaskId,
                    ...encryptedFields,
                    employeeId: taskForm.employeeId,
                    amount: selectedEmployee.monthlySalary || 0,
                    deadline: deadlineIso,
                    escrowId,
                    txHash,
                    contractAddress,
                    chainId: String(chainId),
                    paymentMode: 'payroll_assignment',
                });

                setTaskForm(emptyTaskForm);
                setTaskFormErrors({});
                fireSuccessToast('Task assigned (payroll-covered, no per-task escrow).');
            } else {
                let liveToken =
                    (await refreshTokenBalanceForFunding()) || tokenInfo;

                const taskAmount = ethers.parseUnits(
                    String(taskForm.amount),
                    liveToken.decimals,
                );

                if (liveToken.balance < taskAmount) {
                    setTaskFormSubmitError(
                        `Your wallet balance is lower than the requested ${liveToken.symbol} amount.`,
                    );
                    return;
                }

                const { escrowId, txHash } = await fundTaskEscrowOnChain({
                    taskId: draftTaskId,
                    amount: taskForm.amount,
                    deadline: deadlineIso,
                    employeeRecord: selectedEmployee,
                    tokenSnapshot: liveToken,
                });

                await backendApi.createTask(backendToken, {
                    id: draftTaskId,
                    ...encryptedFields,
                    employeeId: taskForm.employeeId,
                    amount: taskForm.amount,
                    deadline: deadlineIso,
                    escrowId,
                    txHash,
                    contractAddress,
                    chainId: String(chainId),
                    paymentMode: 'lump_sum',
                });

                setTaskForm(emptyTaskForm);
                setTaskFormErrors({});
                fireSuccessToast('Task created and escrow funded on-chain.');
            }

            openCompanyJobRoute({
                section: 'workspace',
                panel: 'tasks',
                view: 'company',
            });
            await refreshAll();
        } catch (error) {
            const message = getReadableError(error);
            setTaskFormSubmitError(message);
            fireErrorToast(message);
            await refreshAll();
        } finally {
            createTaskInFlightRef.current = false;
            setSubmitting(false);
        }
    };

    const handleAsyncAction = async (
        key,
        action,
        successMessage,
        { keepDetailOpen = false } = {},
    ) => {
        try {
            setActiveAction(key);
            await action();
            if (successMessage) fireSuccessToast(successMessage);
            if (!keepDetailOpen) closeTaskDetail();
            await refreshAll();
        } catch (error) {
            fireErrorToast(getReadableError(error));
        } finally {
            setActiveAction('');
        }
    };

    const runRaiseDisputeOnChain = async (task, payload = {}) => {
        if (!canRaiseJobDispute(task)) {
            fireErrorToast('Accept the task before raising a dispute.');
            return;
        }
        const actionKeyBase = `${task.id}-raise-dispute`;
        await handleAsyncAction(
            actionKeyBase,
            async () => {
                const txHash = await writeContractAsync({
                    address: contractAddress,
                    abi: TaskEscrowABI,
                    functionName: JOB_ESCROW_FN.raiseJobDispute,
                    args: [Number(task.escrowId)],
                    chainId: targetChainId,
                });
                await waitForSuccessfulTransactionReceipt(txHash);
                await backendApi.raiseDispute(backendToken, task.id, {
                    ...payload,
                    txHash,
                });
                setSearchParams(
                    (prev) => {
                        const n = new URLSearchParams(prev);
                        n.set('taskId', String(task.id));
                        n.set('panel', 'dispute');
                        return n;
                    },
                    { replace: false },
                );
            },
            'Dispute raised — discussion room is open.',
            { keepDetailOpen: true },
        );
    };

    const runRequestReworkOnChain = async (task, { reason }) => {
        const trimmedReason = String(reason || '').trim();
        if (!task || !trimmedReason) return;

        await handleAsyncAction(
            `task-${task.id}-request-rework`,
            async () => {
                const reasonURI = `backend://task/${task.id}/rework/${crypto.randomUUID()}`;
                const reasonHash = ethers.keccak256(
                    ethers.toUtf8Bytes(
                        JSON.stringify({
                            taskId: task.id,
                            reason: trimmedReason,
                        }),
                    ),
                );

                await publicClient.simulateContract({
                    account: walletAddress,
                    address: contractAddress,
                    abi: TaskEscrowABI,
                    functionName: JOB_ESCROW_FN.requestJobRework,
                    args: [Number(task.escrowId), reasonURI, reasonHash],
                    chainId: targetChainId,
                });

                const txHash = await writeContractAsync({
                    address: contractAddress,
                    abi: TaskEscrowABI,
                    functionName: JOB_ESCROW_FN.requestJobRework,
                    args: [Number(task.escrowId), reasonURI, reasonHash],
                    chainId: targetChainId,
                });
                await waitForSuccessfulTransactionReceipt(txHash);
                await backendApi.rejectTask(backendToken, task.id, {
                    reason: trimmedReason,
                    reworkTxHash: txHash,
                    reasonURI,
                    reasonHash,
                });
            },
            'Rework requested successfully.',
        );
    };

    const handleTaskAction = (task, actionType) => {
        const actionKeyBase = `task-${task.id}-${actionType}`;

        if (actionType === 'fund-escrow') {
            handleAsyncAction(
                actionKeyBase,
                () =>
                    createEscrowForTask({
                        task,
                        employeeRecord: resolveTaskEmployeeRecord(
                            task,
                            task.employee?.wallet,
                        ),
                    }),
                'Escrow funded successfully.',
            );
            return;
        }
        if (actionType === 'accept-task') {
            handleEmployeeAcceptTask(task, {
                actionKey: actionKeyBase,
            });
            return;
        }
        if (actionType === 'accept-escrow-onchain') {
            handleEmployeeAcceptTask(task, {
                actionKey: actionKeyBase,
                successMessage:
                    resolveTaskStatus(task) === 'pending'
                        ? 'Task accepted successfully.'
                        : 'Escrow confirmed on-chain.',
            });
            return;
        }
        if (actionType === 'submit-task') {
            openSubmitTaskForm(task);
            return;
        }
        if (actionType === 'request-rework') {
            setRequestReworkModal({ open: true, task });
            return;
        }
        if (actionType === 'reject-task') {
            if (isAdmin || task.escrowId) {
                fireInfoToast(
                    'Use Request rework for escrow tasks.',
                );
                return;
            }
            handleAsyncAction(
                actionKeyBase,
                () => backendApi.rejectTask(backendToken, task.id),
                'Task rejected successfully.',
            );
            return;
        }
        if (actionType === 'delete-task') {
            if (
                !window.confirm(
                    'Delete this task? This works only for non-funded draft tasks and cannot be undone.',
                )
            ) {
                return;
            }
            handleAsyncAction(
                actionKeyBase,
                () => backendApi.deleteTask(backendToken, task.id),
                'Task removed successfully.',
            );
            return;
        }
        if (actionType === 'complete-task') {
            if (task.escrowId && isEscrowAcceptedOnChain(task) === false) {
                fireInfoToast(
                    'The employee must confirm the escrow on-chain first (use “Confirm on-chain escrow” on their task, or re-open Accept if still pending).',
                );
                return;
            }
            handleAsyncAction(
                actionKeyBase,
                async () => {
                    const txHash = await writeContractAsync({
                        address: contractAddress,
                        abi: TaskEscrowABI,
                        functionName: JOB_ESCROW_FN.releaseJobEscrow,
                        args: [Number(task.escrowId)],
                        chainId: targetChainId,
                    });
                    await waitForSuccessfulTransactionReceipt(txHash);
                    await backendApi.completeTask(backendToken, task.id, {
                        releaseTxHash: txHash,
                    });
                },
                'Task completed and payment released successfully.',
            );
            return;
        }
        if (actionType === 'claim-payment') {
            if (!canEmployeeClaimJobPayment(task, normalizedWallet)) {
                fireInfoToast(
                    'Payment is claimable only after the review window expires.',
                );
                return;
            }
            handleAsyncAction(
                actionKeyBase,
                async () => {
                    await publicClient.simulateContract({
                        account: walletAddress,
                        address: contractAddress,
                        abi: TaskEscrowABI,
                        functionName: JOB_ESCROW_FN.claimJobPayment,
                        args: [Number(task.escrowId)],
                        chainId: targetChainId,
                    });
                    const txHash = await writeContractAsync({
                        address: contractAddress,
                        abi: TaskEscrowABI,
                        functionName: JOB_ESCROW_FN.claimJobPayment,
                        args: [Number(task.escrowId)],
                        chainId: targetChainId,
                    });
                    await waitForSuccessfulTransactionReceipt(txHash);
                    await backendApi.claimTaskPayment(backendToken, task.id, {
                        claimTxHash: txHash,
                    });
                },
                'Payment claimed successfully.',
            );
            return;
        }
        if (actionType === 'request-refund') {
            if (isEscrowAcceptedOnChain(task) !== true) {
                fireInfoToast(
                    'The employee must accept the task on-chain before you can request a refund. If they have not accepted yet, wait for the reclaim window or use Withdraw escrow after the deadline.',
                );
                return;
            }
            handleAsyncAction(
                actionKeyBase,
                async () => {
                    const txHash = await writeContractAsync({
                        address: contractAddress,
                        abi: TaskEscrowABI,
                        functionName: JOB_ESCROW_FN.requestJobRefund,
                        args: [Number(task.escrowId)],
                        chainId: targetChainId,
                    });
                    await waitForSuccessfulTransactionReceipt(txHash);
                    await backendApi.requestRefund(backendToken, task.id, {
                        txHash,
                    });
                },
                'Refund requested successfully.',
            );
            return;
        }
        if (actionType === 'cancel-escrow') {
            if (!canCompanyReclaimUnacceptedEscrow(task, acceptReclaimDelaySeconds)) {
                const countdown = formatReclaimCountdown(task, acceptReclaimDelaySeconds);
                fireInfoToast(
                    countdown
                        ? `Withdraw opens in ${countdown} if the employee has not accepted.`
                        : 'Withdraw is only available after the accept window if the employee has not accepted.',
                );
                return;
            }
            handleAsyncAction(
                actionKeyBase,
                async () => {
                    const txHash = await writeContractAsync({
                        address: contractAddress,
                        abi: TaskEscrowABI,
                        functionName: JOB_ESCROW_FN.cancelJobEscrow,
                        args: [
                            Number(task.escrowId),
                            'Employee did not accept within accept window',
                        ],
                        chainId: targetChainId,
                    });
                    await waitForSuccessfulTransactionReceipt(txHash);
                    await backendApi.markPaymentRefunded(backendToken, task.id, {
                        refundTxHash: txHash,
                    });
                },
                'Escrow withdrawn to your wallet.',
            );
            return;
        }
        if (actionType === 'approve-refund') {
            handleAsyncAction(
                actionKeyBase,
                async () => {
                    const txHash = await writeContractAsync({
                        address: contractAddress,
                        abi: TaskEscrowABI,
                        functionName: JOB_ESCROW_FN.approveJobRefund,
                        args: [Number(task.escrowId)],
                        chainId: targetChainId,
                    });
                    await waitForSuccessfulTransactionReceipt(txHash);
                    await backendApi.acceptRefund(backendToken, task.id, {
                        refundTxHash: txHash,
                    });
                },
                'Refund accepted successfully.',
            );
            return;
        }
        if (actionType === 'raise-dispute') {
            setRaiseDisputeModal({ open: true, task });
            return;
        }
        if (actionType === 'reject-refund') {
            handleAsyncAction(
                actionKeyBase,
                async () => {
                    const txHash = await writeContractAsync({
                        address: contractAddress,
                        abi: TaskEscrowABI,
                        functionName: JOB_ESCROW_FN.raiseJobDispute,
                        args: [Number(task.escrowId)],
                        chainId: targetChainId,
                    });
                    await waitForSuccessfulTransactionReceipt(txHash);
                    await backendApi.rejectRefund(backendToken, task.id, {
                        txHash,
                    });
                    setSearchParams(
                        (prev) => {
                            const n = new URLSearchParams(prev);
                            n.set('taskId', String(task.id));
                            n.set('panel', 'dispute');
                            return n;
                        },
                        { replace: false },
                    );
                },
                'Refund rejected and dispute raised.',
                { keepDetailOpen: true },
            );
            return;
        }
        if (
            actionType === 'resolve-employee' ||
            actionType === 'resolve-company'
        ) {
            const favorEmployee = actionType === 'resolve-employee';
            handleAsyncAction(
                actionKeyBase,
                async () => {
                    const txHash = await writeContractAsync({
                        address: contractAddress,
                        abi: TaskEscrowABI,
                        functionName: JOB_ESCROW_FN.resolveJobDispute,
                        args: [Number(task.escrowId), favorEmployee],
                        chainId: targetChainId,
                    });
                    await waitForSuccessfulTransactionReceipt(txHash);
                    await backendApi.resolveTaskDispute(backendToken, task.id, {
                        favorEmployee,
                        resolveTxHash: txHash,
                    });
                },
                favorEmployee
                    ? 'Dispute resolved in employee favor.'
                    : 'Dispute resolved in company favor.',
                { keepDetailOpen: true },
            );
            return;
        }
    };

    const renderTaskCard = (task) => (
        <TaskCardCompact
            task={task}
            tokenSymbol={tokenInfo.symbol}
            onOpenDetail={openTaskDetail}
        />
    );

    return (
        <div className="crypto-page relative min-h-[calc(100vh-80px)] overflow-hidden">
            <style>{`
                @keyframes cj-ws-bar {
                    0%, 100% { transform: translateX(-100%); opacity: 0.55; }
                    50% { transform: translateX(220%); opacity: 1; }
                }
            `}</style>
            <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                {!isConnected ? (
                    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6">
                        <p className="text-sm uppercase tracking-[0.25em] text-teal-700 dark:text-teal-400">
                            Company / Job
                        </p>
                        <h1 className="text-center text-4xl font-extrabold text-zinc-900 dark:text-white">
                            Connect your wallet to manage company tasks and
                            payments.
                        </h1>
                        <ConnectWalletButton />
                    </div>
                ) : chainId !== targetChainId ? (
                    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6">
                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                            Switch to the configured network to use the company
                            job screen.
                        </p>
                        <SwitchNetworkButton />
                    </div>
                ) : authLoading ? (
                    <div className="flex min-h-[55vh] flex-col items-center justify-center px-4 py-16">
                        <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-zinc-200 bg-white/95 p-10 shadow-lg ring-1 ring-teal-500/10 dark:border-zinc-800 dark:bg-zinc-950/95 dark:ring-teal-500/15">
                            <div
                                className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-teal-500/10 blur-2xl dark:bg-teal-400/10"
                                aria-hidden
                            />
                            <div className="relative flex flex-col items-center gap-6">
                                <div className="relative flex h-[4.5rem] w-[4.5rem] items-center justify-center">
                                    <span
                                        className="absolute inset-0 animate-spin rounded-full border-2 border-teal-100 border-t-teal-600 opacity-90 [animation-duration:1.15s] dark:border-teal-950/80 dark:border-t-teal-400"
                                    />
                                    <div className="relative z-[1] flex items-center justify-center rounded-full bg-zinc-50/90 p-2 shadow-inner dark:bg-zinc-900/90">
                                        <Loader
                                            size="38px"
                                            stroke="#0d9488"
                                            track
                                            trackStroke="rgba(148, 163, 184, 0.35)"
                                        />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                                        Loading workspace…
                                    </p>
                                    <p className="mt-2 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                                        Fetching company profile, team, and tasks from the server.
                                    </p>
                                </div>
                                <div className="flex h-1 w-40 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
                                    <div className="h-full w-1/3 rounded-full bg-teal-600 dark:bg-teal-500 [animation:cj-ws-bar_1.4s_ease-in-out_infinite]" />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : selectedTaskId && detailTaskLoading ? (
                    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 py-16">
                        <Loader size="40px" stroke="#0d9488" />
                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                            Loading task…
                        </p>
                    </div>
                ) : selectedTaskId && !selectedTask ? (
                    <div className="mx-auto max-w-lg space-y-4 py-16 text-center">
                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                            Task not found or you no longer have access.
                        </p>
                        <button
                            type="button"
                            onClick={closeTaskDetail}
                            className="text-sm font-semibold text-teal-700 dark:text-teal-400"
                        >
                            Back to tasks
                        </button>
                    </div>
                ) : selectedTask ? (
                    <TaskReviewPage
                        task={selectedTask}
                        acceptReclaimDelaySeconds={acceptReclaimDelaySeconds}
                        tokenSymbol={tokenInfo.symbol}
                        formatNumber={formatNumber}
                        normalizedWallet={normalizedWallet}
                        isAdmin={isAdmin}
                        isDisputeAdmin={isAdmin}
                        authToken={backendToken}
                        signMessageAsync={signMessageAsync}
                        walletAddress={walletAddress}
                        activeAction={activeAction}
                        onTaskAction={handleTaskAction}
                        onBack={closeTaskDetail}
                        onRefreshTask={refreshAll}
                        backLabel={taskReviewBackLabel}
                        showDisputePanel={
                            searchParams.get('panel') === 'dispute' ||
                            resolveTaskStatus(selectedTask) === 'disputed'
                        }
                        openDiscussionOnLoad={
                            searchParams.get('panel') === 'dispute'
                        }
                        showSubmitForm={searchParams.get('panel') === 'submit'}
                        canSubmit={canSubmitSelectedTask}
                        onCloseSubmitForm={closeSubmitTaskForm}
                        onSubmitOnChain={submitTaskWorkOnChain}
                        onSubmitSuccess={async () => {
                            closeSubmitTaskForm();
                            try {
                                await refreshSelectedTask();
                                await refreshAll({ skipLoading: true });
                            } catch (error) {
                                fireErrorToast(getReadableError(error));
                            }
                        }}
                    />
                ) : isEditingEmployee && !selectedEmployee ? (
                    <div className="mx-auto max-w-lg space-y-4 py-16 text-center">
                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                            Employee not found or removed from your team.
                        </p>
                        <button
                            type="button"
                            onClick={closeEditEmployee}
                            className="text-sm font-semibold text-teal-700 dark:text-teal-400"
                        >
                            Back to employees
                        </button>
                    </div>
                ) : isEditingEmployee && selectedEmployee ? (
                    <EditEmployeePage
                        employee={selectedEmployee}
                        form={editEmployeeForm}
                        onChange={updateEditEmployeeForm}
                        onSubmit={handleUpdateEmployeeSubmit}
                        onCancel={closeEditEmployee}
                        onRemove={() => handleRemoveEmployee(selectedEmployee)}
                        submitting={submitting}
                        isCompanyOwner={isCompanyOwner}
                        backendToken={backendToken}
                        inputClass={formFieldClass}
                    />
                ) : selectedEmployeeId && !selectedEmployeeWithTasks ? (
                    <div className="mx-auto max-w-lg space-y-4 py-16 text-center">
                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                            Employee not found or removed from your team.
                        </p>
                        <button
                            type="button"
                            onClick={closeEmployeeDetail}
                            className="text-sm font-semibold text-teal-700 dark:text-teal-400"
                        >
                            Back to employees
                        </button>
                    </div>
                ) : selectedEmployeeWithTasks && !isEditingEmployee ? (
                    <EmployeeDetailPage
                        employee={selectedEmployeeWithTasks}
                        tokenSymbol={tokenInfo.symbol}
                        formatNumber={formatNumber}
                        onOpenTask={openTaskDetail}
                        onBack={closeEmployeeDetail}
                        isCompanyOwner={isCompanyOwner}
                        onEditEmployee={(employee) =>
                            openEditEmployee(employee, { returnTo: 'detail' })
                        }
                        onToggleStatus={handleToggleEmployeeStatus}
                        statusUpdating={
                            employeeStatusUpdatingId === selectedEmployeeWithTasks.id
                        }
                    />
                ) : (
                    <div
                        key={`${activeWorkspaceTab}-${activeSetupPanel}-${searchParams.get('section') || ''}`}
                        className="animate-cj-section relative space-y-10"
                    >
                        {loading && (
                            <div
                                className="absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-white/80 backdrop-blur-sm transition-opacity duration-300 dark:bg-zinc-950/70"
                                aria-busy="true"
                                aria-live="polite"
                            >
                                <div className="relative mx-4 flex max-w-sm flex-col items-center gap-5 rounded-2xl border border-zinc-200 bg-white/95 px-8 py-8 shadow-2xl ring-1 ring-teal-500/10 dark:border-zinc-800 dark:bg-zinc-950/95 dark:ring-teal-500/15">
                                    <div
                                        className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-teal-500/10 blur-2xl dark:bg-teal-400/10"
                                        aria-hidden
                                    />
                                    <div className="relative flex h-14 w-14 items-center justify-center">
                                        <span
                                            className="absolute inset-0 animate-spin rounded-full border-2 border-teal-100 border-t-teal-600 opacity-90 [animation-duration:1.1s] dark:border-teal-950/80 dark:border-t-teal-400"
                                        />
                                        <div className="relative z-[1] flex items-center justify-center rounded-full bg-zinc-50/90 p-1.5 shadow-inner dark:bg-zinc-900/90">
                                            <Loader
                                                size="34px"
                                                stroke="#0d9488"
                                                track
                                                trackStroke="rgba(148, 163, 184, 0.35)"
                                            />
                                        </div>
                                    </div>
                                    <div className="relative text-center">
                                        <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                                            Syncing workspace…
                                        </p>
                                        <p className="mt-1.5 text-center text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                                            Company profile, team directory, and tasks
                                        </p>
                                    </div>
                                    <div className="relative flex h-1 w-36 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
                                        <div className="h-full w-1/3 rounded-full bg-teal-600 dark:bg-teal-500 [animation:cj-ws-bar_1.4s_ease-in-out_infinite]" />
                                    </div>
                                </div>
                            </div>
                        )}
                        {effectiveJoinMode === 'employee' &&
                            !showJoinedDashboard &&
                            activeWorkspaceTab !== 'stats' &&
                            activeWorkspaceTab !== 'history' &&
                            activeWorkspaceTab !== 'earnings' &&
                            activeWorkspaceTab !== 'profile' &&
                            !(
                                activeWorkspaceTab === 'workspace' &&
                                workspacePagePanel === 'activity'
                            ) &&
                            !(
                                activeWorkspaceTab === 'workspace' &&
                                workspacePagePanel === 'tasks' &&
                                activeTaskView === 'disputed'
                            ) && (
                                <section className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                                    {[
                                        {
                                            label: 'Locked by Company',
                                            value: `${formatNumber(stats.totalLocked)} ${tokenInfo.symbol}`,
                                            accent: 'sky',
                                            icon: 'fa-solid fa-lock',
                                        },
                                        {
                                            label: 'Received',
                                            value: `${formatNumber(stats.totalEarned)} ${tokenInfo.symbol}`,
                                            accent: 'emerald',
                                            icon: 'fa-solid fa-arrow-trend-up',
                                        },
                                        {
                                            label: 'Awaiting Release',
                                            value: stats.awaitingRelease,
                                            accent: 'slate',
                                            icon: 'fa-regular fa-clock',
                                        },
                                        {
                                            label: 'Open Disputes',
                                            value: stats.openDisputes,
                                            accent: 'rose',
                                            icon: 'fa-solid fa-triangle-exclamation',
                                        },
                                    ].map((item) => {
                                        const surface = {
                                            slate: 'border-zinc-200 bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-900/50',
                                            sky: 'border-sky-200 bg-sky-50 dark:border-sky-500/15 dark:bg-sky-500/[0.04]',
                                            emerald:
                                                'border-emerald-200 bg-emerald-50 dark:border-emerald-500/15 dark:bg-emerald-500/[0.04]',
                                            rose: 'border-rose-200 bg-rose-50 dark:border-rose-500/15 dark:bg-rose-500/[0.04]',
                                        }[item.accent];
                                        const iconTone = {
                                            slate: 'text-zinc-500 dark:text-zinc-500',
                                            sky: 'text-sky-600 dark:text-sky-400',
                                            emerald: 'text-emerald-600 dark:text-emerald-400',
                                            rose: 'text-rose-600 dark:text-rose-400',
                                        }[item.accent];
                                        const valueColor =
                                            item.accent === 'rose'
                                                ? 'text-rose-700 dark:text-rose-400'
                                                : 'text-zinc-900 dark:text-white';
                                        return (
                                            <div
                                                key={item.label}
                                                className={`rounded-2xl border ${surface} px-5 py-5`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <p className="text-xs text-zinc-600 dark:text-zinc-400">
                                                        {item.label}
                                                    </p>
                                                    <i
                                                        className={`${item.icon} text-sm ${iconTone}`}
                                                    />
                                                </div>
                                                <p
                                                    className={`mt-2 text-2xl font-bold tabular-nums ${valueColor}`}
                                                >
                                                    {item.value}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </section>
                            )}

                        {!contractConfigured && (
                            <section className="rounded-[28px] border border-amber-300/60 bg-amber-50 p-6 dark:border-amber-500/30 dark:bg-amber-500/10">
                                <h2 className="text-xl font-bold text-zinc-900 dark:text-white">
                                    Payments setup required
                                </h2>
                                <p className="mt-3 max-w-3xl text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                                    Complete the payment configuration to enable
                                    funding and settlement actions.
                                </p>
                            </section>
                        )}

                        {activeWorkspaceTab === 'setup' && showJoinedDashboard && (
                            <JobsWorkspaceDashboard
                                role={effectiveJoinMode === 'company' ? 'company' : 'employee'}
                                company={company}
                                employeeCompany={employeeCompany}
                                walletAddress={walletAddress}
                                tokenSymbol={tokenInfo.symbol}
                                formatNumber={formatNumber}
                                notifications={notifications}
                                stats={stats}
                                companyTasks={companyTasks}
                                employees={employees}
                                employeeTasks={employeeTasks}
                                openCompanyJobRoute={openCompanyJobRoute}
                                onCreateTask={() =>
                                    openCompanyJobRoute({ section: 'create' })
                                }
                                onOpenTask={openTaskDetail}
                                payrollCard={
                                    effectiveJoinMode === 'employee' ? (
                                        <EmployeePayrollCard
                                            payrollStatus={employeePayrollStatus}
                                            tokenSymbol={tokenInfo.symbol}
                                            loading={employeePayrollLoading}
                                            claiming={employeePayrollClaiming}
                                            onClaim={handleClaimPayroll}
                                            onRefresh={refreshEmployeePayrollStatus}
                                        />
                                    ) : null
                                }
                            />
                        )}

                        {activeWorkspaceTab === 'setup' && !showJoinedDashboard && (
                            <section
                                className={`grid gap-6 ${hasJoinSelection &&
                                    (activeSetupPanel === 'company' ||
                                        activeSetupPanel === 'employee' ||
                                        activeSetupPanel === 'add-employee' ||
                                        activeSetupPanel === 'edit-employee')
                                    ? 'xl:grid-cols-1'
                                    : 'xl:grid-cols-[1fr_1fr]'
                                    }`}
                            >
                                {/* LEFT: Join / Access (hidden when joined user opens org panels) */}
                                {!(
                                    hasJoinSelection &&
                                    (activeSetupPanel === 'company' ||
                                        activeSetupPanel === 'employee' ||
                                        activeSetupPanel === 'add-employee' ||
                                        activeSetupPanel === 'edit-employee')
                                ) && (
                                        <section className="rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 dark:shadow-none p-6">
                                            <div className="flex items-end justify-between gap-4">
                                                <div>
                                                    <h2 className="text-2xl font-bold text-zinc-900 dark:text-white">
                                                        {company
                                                            ? 'Company Owner'
                                                            : employeeCompany
                                                                ? 'Employee Access'
                                                                : joinChoice
                                                                    ? 'Join Setup'
                                                                    : 'Join Workspace'}
                                                    </h2>
                                                    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                                                        {company
                                                            ? 'Manage your company, employees, and tasks.'
                                                            : employeeCompany
                                                                ? `You are an employee of ${employeeCompany.name}.`
                                                                : joinChoice === 'company'
                                                                    ? 'Create your company profile first.'
                                                                    : joinChoice === 'employee'
                                                                        ? 'You will be added by a company owner.'
                                                                        : 'Choose how you want to join.'}
                                                    </p>
                                                </div>
                                                <div className="text-xs text-zinc-500 dark:text-zinc-500">
                                                    {loading
                                                        ? 'Refreshing...'
                                                        : `${notifications.length} notification${notifications.length === 1 ? '' : 's'}`}
                                                </div>
                                            </div>

                                            {!company &&
                                                !employeeCompany &&
                                                !joinChoice && (
                                                    <>
                                                        <p className="mt-5 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                                            CronPay separates{' '}
                                                            <span className="font-medium text-zinc-800 dark:text-zinc-200">
                                                                organization setup
                                                            </span>{' '}
                                                            from{' '}
                                                            <span className="font-medium text-zinc-800 dark:text-zinc-200">
                                                                contributor access
                                                            </span>
                                                            . Pick the path that matches how you will work on-chain.
                                                        </p>
                                                        <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                                            <div className="relative overflow-hidden rounded-2xl border border-teal-200/80 bg-gradient-to-br from-teal-50/90 to-white p-5 dark:border-teal-900/40 dark:from-teal-950/50 dark:to-zinc-950">
                                                                <div className="flex items-center gap-2.5">
                                                                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-600 text-white shadow-sm dark:bg-teal-500">
                                                                        <i className="fa-solid fa-building text-sm" />
                                                                    </span>
                                                                    <div>
                                                                        <p className="text-xs font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-300">
                                                                            Join as company
                                                                        </p>
                                                                        <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                                                                            Run payroll & tasks
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <ul className="mt-4 space-y-2.5 text-xs leading-relaxed text-zinc-700 dark:text-zinc-300">
                                                                    <li className="flex gap-2">
                                                                        <i className="fa-solid fa-check mt-0.5 shrink-0 text-teal-600 dark:text-teal-400" />
                                                                        <span>
                                                                            Register your company, add employees with wallets and rates, then create and fund escrow-backed tasks.
                                                                        </span>
                                                                    </li>
                                                                    <li className="flex gap-2">
                                                                        <i className="fa-solid fa-check mt-0.5 shrink-0 text-teal-600 dark:text-teal-400" />
                                                                        <span>
                                                                            You approve milestones, release USDC when work is submitted, and resolve disputes from one workspace.
                                                                        </span>
                                                                    </li>
                                                                    <li className="flex gap-2">
                                                                        <i className="fa-solid fa-check mt-0.5 shrink-0 text-teal-600 dark:text-teal-400" />
                                                                        <span>
                                                                            Best if you control the treasury wallet that will pay contractors and staff.
                                                                        </span>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                            <div className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-zinc-50 to-white p-5 dark:border-zinc-700 dark:from-zinc-900/60 dark:to-zinc-950">
                                                                <div className="flex items-center gap-2.5">
                                                                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-200 bg-white text-zinc-700 shadow-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200">
                                                                        <i className="fa-solid fa-user-check text-sm" />
                                                                    </span>
                                                                    <div>
                                                                        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                                                                            Join as employee
                                                                        </p>
                                                                        <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                                                                            Get paid on milestones
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <ul className="mt-4 space-y-2.5 text-xs leading-relaxed text-zinc-700 dark:text-zinc-300">
                                                                    <li className="flex gap-2">
                                                                        <i className="fa-solid fa-check mt-0.5 shrink-0 text-zinc-500 dark:text-zinc-400" />
                                                                        <span>
                                                                            Your manager adds you to the team directory with your wallet no company signup on your side.
                                                                        </span>
                                                                    </li>
                                                                    <li className="flex gap-2">
                                                                        <i className="fa-solid fa-check mt-0.5 shrink-0 text-zinc-500 dark:text-zinc-400" />
                                                                        <span>
                                                                            You will see tasks assigned to you, submit work for review, and receive payouts when the company releases escrow.
                                                                        </span>
                                                                    </li>
                                                                    <li className="flex gap-2">
                                                                        <i className="fa-solid fa-check mt-0.5 shrink-0 text-zinc-500 dark:text-zinc-400" />
                                                                        <span>
                                                                            Choose this if you are a contractor or staff member joining an existing organization.
                                                                        </span>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                            {!company &&
                                                !employeeCompany &&
                                                !joinChoice && (
                                                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setJoinChoice('company');
                                                                openCompanyModal();
                                                            }}
                                                            className="rounded-xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                                                        >
                                                            Join as Company
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setJoinChoice('employee');
                                                            }}
                                                            className="rounded-xl border border-zinc-300 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                                        >
                                                            Join as Employee
                                                        </button>
                                                    </div>
                                                )}

                                            {/* Quick path buttons */}
                                            {effectiveJoinMode && (
                                                <div className="mt-6 flex flex-wrap gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            openCompanyJobRoute({
                                                                section: 'workspace',
                                                                panel: 'tasks',
                                                                view:
                                                                    effectiveJoinMode ===
                                                                    'company'
                                                                        ? 'company'
                                                                        : 'assigned',
                                                            })
                                                        }
                                                        className="rounded-xl border border-zinc-300 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                                    >
                                                        Go to Workspace
                                                    </button>
                                                </div>
                                            )}
                                        </section>
                                    )}

                                {/* RIGHT: Section content based on sidebar child menu */}
                                <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 dark:shadow-none space-y-6">

                                    {activeSetupPanel === 'company' ? (
                                        <>
                                            {/* Header */}
                                            <div className="space-y-1">
                                                <p className="text-xs uppercase tracking-wide text-zinc-600 dark:text-zinc-400">
                                                    Company Profile
                                                </p>
                                                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                                    Company Details
                                                </h3>
                                            </div>

                                            {company ? (
                                                <div className="space-y-4">

                                                    {/* Info Cards */}
                                                    <div className="grid gap-3 sm:grid-cols-2">

                                                        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900/40">
                                                            <p className="text-xs text-zinc-600 dark:text-zinc-400">
                                                                Company name
                                                            </p>
                                                            <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-white">
                                                                {company.name}
                                                            </p>
                                                        </div>

                                                        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900/40">
                                                            <p className="text-xs text-zinc-600 dark:text-zinc-400">
                                                                Team size
                                                            </p>
                                                            <p className="mt-1 text-sm font-medium text-zinc-900 dark:text-white">
                                                                {company.employeeSize || 'Not set'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Button */}
                                                    <button
                                                        onClick={() => openCompanyModal()}
                                                        className="w-full rounded-xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 sm:w-auto dark:bg-teal-600 dark:hover:bg-teal-500"
                                                    >
                                                        Update Company
                                                    </button>

                                                </div>
                                            ) : (
                                                <div className="space-y-4">

                                                    {/* Empty */}
                                                    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 py-8 text-center dark:border-zinc-800 dark:bg-zinc-900/40">
                                                        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-600/60 bg-zinc-800/60">
                                                            <i className="fa-regular fa-building text-zinc-400 dark:text-zinc-400"></i>
                                                        </div>
                                                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                                                            No company profile yet
                                                        </p>
                                                    </div>

                                                    <button
                                                        onClick={() => openCompanyModal()}
                                                        disabled={effectiveJoinMode !== 'company'}
                                                        className="w-full rounded-xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 disabled:opacity-50 sm:w-auto dark:bg-teal-600 dark:hover:bg-teal-500"
                                                    >
                                                        Create Company
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : activeSetupPanel === 'add-employee' ? (
                                        <AddEmployeePage
                                            form={employeeForm}
                                            onChange={updateEmployeeForm}
                                            onSubmit={handleEmployeeSubmit}
                                            onCancel={closeAddEmployee}
                                            submitting={submitting}
                                            hasCompany={Boolean(company)}
                                            backendToken={backendToken}
                                            inputClass={formFieldClass}
                                        />
                                    ) : activeSetupPanel === 'employee' ? (
                                        <EmployeesDirectory
                                            employees={employees}
                                            tokenSymbol={tokenInfo.symbol}
                                            isCompanyOwner={isCompanyOwner}
                                            canAddEmployee={
                                                Boolean(company) &&
                                                effectiveJoinMode === 'company'
                                            }
                                            submitting={submitting}
                                            statusUpdatingId={employeeStatusUpdatingId}
                                            onAddEmployee={openAddEmployee}
                                            onViewEmployee={openEmployeeDetail}
                                            onEditEmployee={(employee) =>
                                                openEditEmployee(employee, {
                                                    returnTo: 'list',
                                                })
                                            }
                                            onToggleStatus={handleToggleEmployeeStatus}
                                            onFundPayroll={handleFundEmployeePayroll}
                                            payrollFundingId={payrollFundingId}
                                            onRemoveEmployee={handleRemoveEmployee}
                                        />

                                    ) : (
                                        <>
                                            {/* Header */}
                                            <div className="space-y-1">
                                                <p className="text-xs uppercase tracking-wide text-zinc-600 dark:text-zinc-400">
                                                    Account Overview
                                                </p>
                                                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                                    Company / Job Access
                                                </h3>
                                            </div>

                                            <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900/40 space-y-2">
                                                <p className="text-sm font-medium text-zinc-900 dark:text-white">
                                                    Access
                                                </p>
                                                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                                                    {company
                                                        ? 'Company owner mode.'
                                                        : employeeCompany
                                                            ? 'Employee mode (company actions disabled).'
                                                            : joinChoice === 'employee'
                                                                ? 'Employee mode (waiting for assignment).'
                                                                : joinChoice === 'company'
                                                                    ? 'Company setup mode.'
                                                                    : 'Select join type.'}
                                                </p>
                                            </div>
                                            {!company &&
                                                !employeeCompany &&
                                                !joinChoice && (
                                                    <div className="mt-5 space-y-4">
                                                        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 dark:text-zinc-500">
                                                            Typical first-time flow
                                                        </p>
                                                        <div className="grid gap-3 sm:grid-cols-3">
                                                            {[
                                                                {
                                                                    step: '1',
                                                                    title: 'Pick a path',
                                                                    body: 'Company owners configure the org; employees wait for an invite.',
                                                                    icon: 'fa-solid fa-route',
                                                                },
                                                                {
                                                                    step: '2',
                                                                    title: 'Connect & verify',
                                                                    body: 'We link this wallet to your role so balances and tasks stay in sync.',
                                                                    icon: 'fa-solid fa-link',
                                                                },
                                                                {
                                                                    step: '3',
                                                                    title: 'Work in escrow',
                                                                    body: 'Tasks move through funded escrow transparent releases when milestones complete.',
                                                                    icon: 'fa-solid fa-coins',
                                                                },
                                                            ].map((item) => (
                                                                <div
                                                                    key={item.step}
                                                                    className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900/50"
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 text-xs font-bold text-teal-700 dark:bg-zinc-800 dark:text-teal-400">
                                                                            {item.step}
                                                                        </span>
                                                                        <i
                                                                            className={`${item.icon} text-sm text-zinc-500 dark:text-zinc-400`}
                                                                        />
                                                                    </div>
                                                                    <p className="mt-3 text-sm font-semibold text-zinc-900 dark:text-white">
                                                                        {item.title}
                                                                    </p>
                                                                    <p className="mt-1.5 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                                                                        {item.body}
                                                                    </p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className="flex gap-2 rounded-xl border border-teal-200/70 bg-teal-50/80 p-3 text-xs leading-relaxed text-teal-900 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-100">
                                                            <i className="fa-solid fa-shield-halved mt-0.5 shrink-0 text-teal-600 dark:text-teal-400" />
                                                            <span>
                                                                Use the same wallet you will use on-chain for payouts. Switching accounts later may require your admin to update team records.
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                        </>
                                    )}
                                </section>

                                {!company && !employeeCompany && !joinChoice && (
                                    <div className="xl:col-span-2 overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-zinc-50 via-white to-teal-50/40 p-6 shadow-sm dark:border-zinc-800 dark:from-zinc-950 dark:via-zinc-950 dark:to-teal-950/20 dark:shadow-none sm:p-8">
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                                            <div>
                                                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                                    Why two join options?
                                                </h3>
                                                <p className="mt-1 max-w-2xl text-sm text-zinc-600 dark:text-zinc-400">
                                                    One workspace supports both sides of a payroll relationship without mixing permissions.
                                                </p>
                                            </div>
                                            <div className="hidden text-4xl text-teal-200/80 sm:block dark:text-teal-900/40" aria-hidden>
                                                <i className="fa-solid fa-diagram-project" />
                                            </div>
                                        </div>
                                        <div className="mt-6 grid gap-4 border-t border-zinc-200/80 pt-6 dark:border-zinc-800 sm:grid-cols-2">
                                            <div className="flex gap-4 rounded-xl border border-zinc-200/90 bg-white/80 p-4 dark:border-zinc-800 dark:bg-zinc-900/60">
                                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-600 text-white shadow-sm dark:bg-teal-500">
                                                    <i className="fa-solid fa-sitemap text-lg" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                                                        Company path = control plane
                                                    </p>
                                                    <p className="mt-1 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                                                        Create legal entity metadata, onboard wallets, open escrows, and approve or dispute work from a single owner dashboard.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex gap-4 rounded-xl border border-zinc-200/90 bg-white/80 p-4 dark:border-zinc-800 dark:bg-zinc-900/60">
                                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-zinc-200 bg-zinc-100 text-zinc-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200">
                                                    <i className="fa-solid fa-handshake text-lg" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                                                        Employee path = execution lane
                                                    </p>
                                                    <p className="mt-1 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                                                        See only what is assigned to you, submit proof-of-work, and track releases no company-wide admin tools exposed by mistake.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {employeeCompany &&
                                    effectiveJoinMode === 'employee' &&
                                    !company && (
                                        <div className="xl:col-span-2 space-y-4">
                                        <EmployeePayrollCard
                                            payrollStatus={employeePayrollStatus}
                                            tokenSymbol={tokenInfo.symbol}
                                            loading={employeePayrollLoading}
                                            claiming={employeePayrollClaiming}
                                            onClaim={handleClaimPayroll}
                                            onRefresh={refreshEmployeePayrollStatus}
                                        />
                                        <div className="overflow-hidden rounded-2xl border border-sky-200/80 bg-gradient-to-br from-sky-50/90 to-white p-6 shadow-sm dark:border-sky-900/40 dark:from-sky-950/30 dark:to-zinc-950 sm:p-8">
                                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                                <div>
                                                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-sky-700 dark:text-sky-300">
                                                        Employee workspace
                                                    </p>
                                                    <h3 className="mt-1 text-lg font-semibold text-zinc-900 dark:text-white">
                                                        You are linked to{' '}
                                                        <span className="text-teal-700 dark:text-teal-400">
                                                            {employeeCompany.name}
                                                        </span>
                                                    </h3>
                                                    <p className="mt-2 max-w-2xl text-sm text-zinc-600 dark:text-zinc-400">
                                                        Owners fund tasks and release escrow when you submit work. Use the{' '}
                                                        <span className="font-medium text-zinc-800 dark:text-zinc-200">
                                                            Tasks
                                                        </span>{' '}
                                                        tab to track assignments and the{' '}
                                                        <span className="font-medium text-zinc-800 dark:text-zinc-200">
                                                            Workspace
                                                        </span>{' '}
                                                        tab for activity on your wallet.
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        openCompanyJobRoute({
                                                            section: 'workspace',
                                                            panel: 'tasks',
                                                            view: 'my',
                                                        })
                                                    }
                                                    className="shrink-0 rounded-xl bg-teal-700 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                                                >
                                                    View my tasks
                                                </button>
                                            </div>
                                            <ul className="mt-5 grid gap-3 border-t border-sky-200/60 pt-5 text-xs text-zinc-700 dark:border-sky-900/40 dark:text-zinc-300 sm:grid-cols-3">
                                                <li className="flex gap-2 rounded-lg bg-white/70 p-3 dark:bg-zinc-900/50">
                                                    <i className="fa-solid fa-list-check mt-0.5 text-teal-600 dark:text-teal-400" />
                                                    <span>
                                                        Accept or decline assignments when they appear under Assigned to Me.
                                                    </span>
                                                </li>
                                                <li className="flex gap-2 rounded-lg bg-white/70 p-3 dark:bg-zinc-900/50">
                                                    <i className="fa-solid fa-paper-plane mt-0.5 text-teal-600 dark:text-teal-400" />
                                                    <span>
                                                        Submit milestones with notes and attachments so reviewers can release funds quickly.
                                                    </span>
                                                </li>
                                                <li className="flex gap-2 rounded-lg bg-white/70 p-3 dark:bg-zinc-900/50">
                                                    <i className="fa-solid fa-scale-balanced mt-0.5 text-teal-600 dark:text-teal-400" />
                                                    <span>
                                                        Escrow stays trust-minimized—disputes route through the same contract rules as direct payments.
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                        </div>
                                    )}

                                <form
                                    onSubmit={handleCompanySubmit}
                                    className="rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 dark:shadow-none p-6 hidden"
                                >
                                    <div className="flex items-end justify-between gap-4">
                                        <div>
                                            <h2 className="text-2xl font-bold text-zinc-900 dark:text-white">
                                                {company
                                                    ? 'Company Profile'
                                                    : 'Create Company'}
                                            </h2>
                                            <p className="text-sm text-zinc-600 dark:text-zinc-400">
                                                Account status:{' '}
                                                {authLoading
                                                    ? 'connecting...'
                                                    : backendToken
                                                        ? 'ready'
                                                        : 'not ready'}
                                            </p>
                                        </div>
                                        <div className="text-xs text-zinc-500 dark:text-zinc-500">
                                            {loading
                                                ? 'Refreshing...'
                                                : `${notifications.length} notification${notifications.length === 1 ? '' : 's'}`}
                                        </div>
                                    </div>
                                    <div className="mt-6 grid gap-4 md:grid-cols-2">
                                        <label className="block">
                                            <span className="mb-2 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                                Company name
                                            </span>
                                            <input
                                                type="text"
                                                name="name"
                                                value={companyForm.name}
                                                onChange={updateCompanyForm}
                                                placeholder="CronPay Labs"
                                                className={formFieldClass}
                                            />
                                        </label>
                                        <label className="block">
                                            <span className="mb-2 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                                Employee size
                                            </span>
                                            <input
                                                type="text"
                                                name="employeeSize"
                                                value={companyForm.employeeSize}
                                                onChange={updateCompanyForm}
                                                placeholder="11-50"
                                                className={formFieldClass}
                                            />
                                        </label>
                                    </div>
                                    <div className="mt-6 flex flex-wrap gap-3">
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="rounded-2xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-teal-800 disabled:opacity-60 dark:bg-teal-600 dark:hover:bg-teal-500"
                                        >
                                            {submitting
                                                ? 'Saving...'
                                                : company
                                                    ? 'Update Company'
                                                    : 'Create Company'}
                                        </button>
                                    </div>
                                </form>

                            </section>
                        )}

                        {hasJoinSelection && activeWorkspaceTab === 'history' && (
                            <TaskHistoryPage
                                role={
                                    effectiveJoinMode === 'company'
                                        ? 'company'
                                        : 'employee'
                                }
                                token={backendToken}
                                hydrateTask={hydrateTaskForDisplay}
                                refreshSignal={taskListRefreshKey}
                                renderTask={renderTaskCard}
                            />
                        )}

                        {hasJoinSelection && activeWorkspaceTab === 'earnings' && (
                            <JobsEarningsPage
                                role={
                                    effectiveJoinMode === 'company'
                                        ? 'company'
                                        : 'employee'
                                }
                                tasks={portalEarningsTasks}
                                tokenSymbol={tokenInfo.symbol}
                                formatNumber={formatNumber}
                                onOpenTask={(taskId) => {
                                    const task = allTasks.find(
                                        (row) => String(row.id) === String(taskId),
                                    );
                                    if (task) openTaskDetail(task);
                                }}
                                onOpenHistory={() =>
                                    openCompanyJobRoute({ section: 'history' })
                                }
                            />
                        )}

                        {hasJoinSelection && activeWorkspaceTab === 'profile' && (
                            <JobsProfilePage
                                role={
                                    effectiveJoinMode === 'company'
                                        ? 'company'
                                        : 'employee'
                                }
                                walletAddress={walletAddress}
                                company={company}
                                employees={employees}
                                employeeCompany={employeeCompany}
                                employeeProfile={employeeProfile}
                                tasks={portalEarningsTasks}
                                tokenSymbol={tokenInfo.symbol}
                                formatNumber={formatNumber}
                                isCompanyOwner={isCompanyOwner}
                                onEditCompany={openCompanyModal}
                                onEditEmployeeProfile={openEmployeeSelfModal}
                                onOpenEarnings={() =>
                                    openCompanyJobRoute({ section: 'earnings' })
                                }
                                onOpenTasks={() =>
                                    openCompanyJobRoute({
                                        section: 'workspace',
                                        panel: 'tasks',
                                        view:
                                            effectiveJoinMode === 'company'
                                                ? 'company'
                                                : 'assigned',
                                    })
                                }
                                onOpenEmployees={() =>
                                    openCompanyJobRoute({
                                        section: 'setup',
                                        panel: 'employee',
                                    })
                                }
                                onOpenDisputed={() =>
                                    openCompanyJobRoute({
                                        section: 'workspace',
                                        panel: 'tasks',
                                        view: 'disputed',
                                    })
                                }
                            />
                        )}

                        {hasJoinSelection &&
                            activeWorkspaceTab === 'stats' &&
                            effectiveJoinMode === 'employee' && (
                            <JobsStatsPage
                                role="employee"
                                tasks={
                                    effectiveJoinMode === 'company'
                                        ? companyTasks
                                        : employeeTasks
                                }
                                tokenSymbol={tokenInfo.symbol}
                                formatNumber={formatNumber}
                            />
                        )}

                        {hasJoinSelection &&
                            activeWorkspaceTab === 'create' &&
                            effectiveJoinMode === 'company' && (
                            <div className="-mx-4 sm:-mx-6 lg:-mx-8">
                            <CreateTaskPage
                                taskForm={taskForm}
                                onChange={handleTaskFormChange}
                                onSubmit={handleCreateTaskSubmit}
                                onCancel={() =>
                                    openCompanyJobRoute({
                                        section: 'setup',
                                        panel: 'overview',
                                    })
                                }
                                taskDraftPreview={taskDraftPreview}
                                tokenSymbol={tokenInfo.symbol}
                                isCompanyOwner={isCompanyOwner}
                                submitting={submitting}
                                backendToken={backendToken}
                                employees={employees}
                                isEmployeeActive={isEmployeeActive}
                                shortenWalletAddress={shortenWalletAddress}
                                errors={taskFormErrors}
                                submitError={taskFormSubmitError}
                                contractConfigured={contractConfigured}
                                hasEnoughBalance={hasEnoughBalance}
                                activeEmployeeCount={activeEmployeeCount}
                                payrollAssignable={payrollAssignable}
                            />
                            </div>
                        )}

                        {hasJoinSelection && activeWorkspaceTab === 'workspace' && (
                            <div className="space-y-6">
                                {workspacePagePanel === 'activity' && (
                                    <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/40 dark:shadow-none">
                                        <div className="border-b border-zinc-100 bg-zinc-50/80 px-5 py-4 dark:border-zinc-800 dark:bg-zinc-950/50">
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
                                                            <i className="fa-solid fa-timeline text-sm" aria-hidden />
                                                        </span>
                                                        <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                                            Activity log
                                                        </h2>
                                                    </div>
                                                    <p className="ps-10 text-sm text-zinc-600 dark:text-zinc-400">
                                                        Tracked events for this workspace, newest first
                                                    </p>
                                                </div>
                                                <div className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
                                                    {notifications.length} event{notifications.length === 1 ? '' : 's'}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="px-5 py-4">
                                            {notifications.length ? (
                                                <ol className="m-0 list-none p-0">
                                                    {notifications.slice(0, 8).map((notification, index, arr) => {
                                                        const isLast = index === arr.length - 1;
                                                        const ts =
                                                            notification.createdAt || notification.updatedAt;
                                                        const title =
                                                            notification.title ||
                                                            notification.message ||
                                                            notification.type ||
                                                            'Workspace event';
                                                        const detail =
                                                            notification.body || notification.description;
                                                        const { icon, ring } =
                                                            workspaceActivityIconMeta(notification);
                                                        const activityHref =
                                                            getNotificationLink(
                                                                notification,
                                                            );
                                                        const activityClickable =
                                                            Boolean(
                                                                notification.taskId,
                                                            );
                                                        const rowClass = `flex gap-4 ${activityClickable ? 'group' : ''}`;
                                                        const content = (
                                                            <>
                                                                <div className="flex w-11 shrink-0 flex-col items-center">
                                                                    <div
                                                                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-white shadow-sm ring-2 ${ring} dark:border-zinc-600 dark:bg-zinc-800`}
                                                                        aria-hidden
                                                                    >
                                                                        <i
                                                                            className={`${getNotificationIcon(notification) || icon} text-[13px]`}
                                                                        />
                                                                    </div>
                                                                    {!isLast ? (
                                                                        <div
                                                                            className="mt-0.5 w-px flex-1 min-h-[1.25rem] bg-zinc-200 dark:bg-zinc-700"
                                                                            aria-hidden
                                                                        />
                                                                    ) : null}
                                                                </div>
                                                                <div
                                                                    className={`min-w-0 flex-1 pt-0.5 ${!isLast ? 'border-b border-zinc-100 pb-5 dark:border-zinc-800/80' : ''}`}
                                                                >
                                                                    <time
                                                                        className="text-[11px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-500"
                                                                        dateTime={ts || undefined}
                                                                    >
                                                                        {formatDateTime(ts)}
                                                                    </time>
                                                                    <p className="mt-1 text-sm font-semibold text-zinc-900 group-hover:text-teal-800 dark:text-zinc-100 dark:group-hover:text-teal-300">
                                                                        {title}
                                                                    </p>
                                                                    {detail ? (
                                                                        <p className="mt-1.5 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                                                                            {detail}
                                                                        </p>
                                                                    ) : null}
                                                                    {activityClickable ? (
                                                                        <p className="mt-2 text-xs font-semibold text-teal-700 dark:text-teal-400">
                                                                            View task
                                                                            <i className="fa-solid fa-arrow-right ms-1 text-[10px]" />
                                                                        </p>
                                                                    ) : null}
                                                                </div>
                                                            </>
                                                        );
                                                        return (
                                                            <li
                                                                key={
                                                                    notification.id ||
                                                                    `${notification.createdAt}-${index}`
                                                                }
                                                                className={rowClass}
                                                            >
                                                                {activityClickable ? (
                                                                    <Link
                                                                        to={activityHref}
                                                                        className="flex w-full gap-4 rounded-lg transition-colors hover:bg-teal-50/50 dark:hover:bg-teal-950/20"
                                                                    >
                                                                        {content}
                                                                    </Link>
                                                                ) : (
                                                                    content
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ol>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-zinc-200 bg-zinc-50/50 py-10 text-center dark:border-zinc-700 dark:bg-zinc-950/30">
                                                    <div className="flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                                                        <i className="fa-regular fa-bell text-zinc-400 dark:text-zinc-500" />
                                                    </div>
                                                    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                                        No activity yet
                                                    </p>
                                                    <p className="max-w-sm text-xs text-zinc-500 dark:text-zinc-500">
                                                        When tasks, funding, or team updates occur, they will
                                                        appear here in order.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}

                                {workspacePagePanel === 'tasks' && (
                                    <>
                                        {taskViews.length > 1 &&
                                        activeTaskView !== 'disputed' &&
                                        activeTaskView !== 'company' ? (
                                        <section className="space-y-5 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-900/40">
                                            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                                                <div className="space-y-1">
                                                    <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                                        Task Collections
                                                    </h2>
                                                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                                                        Switch between focused task views instead of scanning everything
                                                    </p>
                                                </div>

                                                <div className="flex flex-wrap gap-2">
                                                    {taskViews.map((view) => {
                                                        const isActive = activeTaskView === view.key;

                                                        return (
                                                            <button
                                                                key={view.key}
                                                                type="button"
                                                                onClick={() =>
                                                                    openCompanyJobRoute({
                                                                        section: 'workspace',
                                                                        panel: 'tasks',
                                                                        view: view.key,
                                                                    })
                                                                }
                                                                className={`px-4 py-2 rounded-xl text-left transition-colors border ${isActive
                                                                    ? 'border-teal-300 bg-teal-50 text-zinc-900 shadow-inner ring-1 ring-teal-200 dark:border-teal-800 dark:bg-teal-950/50 dark:text-white dark:ring-teal-900/30'
                                                                    : 'border-zinc-200 bg-zinc-100 text-zinc-700 hover:border-zinc-300 hover:bg-zinc-200 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300 dark:hover:border-zinc-600 dark:hover:bg-zinc-800/50'
                                                                    }`}
                                                            >
                                                                <p className="text-sm font-medium">
                                                                    {view.title}
                                                                </p>
                                                                <p className="text-xs text-zinc-500 dark:text-zinc-500">
                                                                    {view.items.length} record{view.items.length === 1 ? '' : 's'}
                                                                </p>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </section>
                                        ) : null}

                                        {selectedTaskView && (
                                            <PaginatedTaskSection
                                                key={`${selectedTaskView.key}-${taskListRefreshKey}`}
                                                token={backendToken}
                                                fetchTasks={resolveTaskListFetcher(
                                                    selectedTaskView.key,
                                                )}
                                                scope={resolveTaskListScope(
                                                    selectedTaskView.key,
                                                )}
                                                assignedToMe={selectedTaskView.key === 'my'}
                                                hydrateTask={hydrateTaskForDisplay}
                                                refreshSignal={taskListRefreshKey}
                                                title={selectedTaskView.title}
                                                description={selectedTaskView.description}
                                                emptyMessage={selectedTaskView.emptyMessage}
                                                renderTask={renderTaskCard}
                                                showStatusFilter={
                                                    selectedTaskView.key !== 'disputed' &&
                                                    selectedTaskView.key !== 'admin-disputes'
                                                }
                                                searchPlaceholder={
                                                    selectedTaskView.key === 'disputed' ||
                                                    selectedTaskView.key === 'admin-disputes'
                                                        ? 'Search by Task ID, title, description, or assignee…'
                                                        : 'Search by Task ID, title, description, or assignee…'
                                                }
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {companyModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                    onClick={() => setCompanyModalOpen(false)}
                    role="presentation"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="company-modal-title"
                    >
                        <div className="sticky top-0 z-[1] flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-4 dark:border-zinc-800 dark:bg-zinc-950">
                            <h3
                                id="company-modal-title"
                                className="text-lg font-semibold text-zinc-900 dark:text-white"
                            >
                                {company ? 'Update Company' : 'Create Company'}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setCompanyModalOpen(false)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 text-zinc-600 transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                            >
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                        <form onSubmit={handleCompanySubmit} className="space-y-5 px-6 py-6">
                            <CompanyProfileFormFields
                                form={companyForm}
                                onChange={updateCompanyForm}
                                inputClass={formFieldClass}
                                backendToken={backendToken}
                                ownerWallet={walletAddress}
                                disabled={submitting}
                            />
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setCompanyModalOpen(false)}
                                    className="rounded-xl border border-zinc-300 bg-zinc-100 px-4 py-2 text-sm font-semibold text-zinc-800 transition-colors hover:border-zinc-400 hover:bg-zinc-200 dark:border-zinc-600 dark:bg-zinc-800/70 dark:text-zinc-200 dark:hover:border-zinc-500 dark:hover:bg-zinc-700/80"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="rounded-xl bg-teal-700 px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-teal-800 disabled:opacity-50 dark:bg-teal-600 dark:hover:bg-teal-500"
                                >
                                    {submitting ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <EmployeeSelfProfileModal
                open={employeeSelfModalOpen}
                onClose={closeEmployeeSelfProfileModal}
                form={employeeSelfForm}
                onChange={updateEmployeeSelfForm}
                onSubmit={handleEmployeeSelfSubmit}
                submitting={submitting}
                inputClass={formFieldClass}
                backendToken={backendToken}
                walletAddress={walletAddress}
                companyName={employeeCompany?.name}
                dismissible
            />
            <RaiseDisputeModal
                open={raiseDisputeModal.open}
                task={raiseDisputeModal.task}
                submitting={
                    activeAction ===
                    `${raiseDisputeModal.task?.id}-raise-dispute`
                }
                onClose={() =>
                    setRaiseDisputeModal({ open: false, task: null })
                }
                onSubmit={async (payload) => {
                    const task = raiseDisputeModal.task;
                    if (!task) return;
                    setRaiseDisputeModal({ open: false, task: null });
                    await runRaiseDisputeOnChain(task, payload);
                }}
            />
            <RequestReworkModal
                open={requestReworkModal.open}
                task={requestReworkModal.task}
                submitting={
                    activeAction ===
                    `task-${requestReworkModal.task?.id}-request-rework`
                }
                onClose={() =>
                    setRequestReworkModal({ open: false, task: null })
                }
                onSubmit={async (payload) => {
                    const task = requestReworkModal.task;
                    if (!task) return;
                    setRequestReworkModal({ open: false, task: null });
                    await runRequestReworkOnChain(task, payload);
                }}
            />
        </div>
    );
};

export default CompanyJob;
